import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { useNavigate, useLocation } from 'react-router-dom';
import { HIDDEN_LOGIN_PATH } from '../config/hiddenRoute';
import {
    FaUserCircle, FaSearch, FaHome, FaUsers, FaPills,
    FaQrcode, FaSignOutAlt, FaChevronDown,
    FaPlus, FaExclamationTriangle,
    FaCog, FaQuestionCircle, FaMicrophone, FaTimes, FaCheck,
    FaSpinner, FaSync, FaEye, FaEdit, FaTrashAlt, FaCamera,
    FaExclamationCircle, FaFileAlt,
    FaBoxOpen, FaClock, FaFilter, FaBars,
    FaBell, FaUserMd, FaUserPlus, FaStethoscope, FaUserMinus,
} from 'react-icons/fa';
import '../styles/Dashboard.css';
import '../styles/NurseDashboard.css';
import MedicationFlagPhotos from '../components/MedicationFlagPhotos';
import MedicationFlagCardRow from '../components/MedicationFlagCardRow';
import MedicationFlagDetailFields from '../components/MedicationFlagDetailFields';
import '../styles/MedicationFlagRegister.css';
import mainLogo from '../assets/mainLogo.png';

const getApiUrl = () => {
    const fallback = process.env.NODE_ENV === 'production'
        ? 'https://kanang-alalay-backend.onrender.com/api'
        : 'http://localhost:5000/api';
    const raw = process.env.REACT_APP_API_URL || fallback;
    const trimmed = raw.replace(/\/+$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const API = getApiUrl();

const STATUS_COLOR = {
    completed: '#1E7D56', administered: '#1E7D56',
    pending: '#E65100', scheduled: '#0277BD',
    upcoming: '#0277BD', overdue: '#C0392B',
    delayed: '#856404', missed: '#C0392B',
    skipped: '#6B7280', alert: '#D94E1B',
    critical: '#C0392B', stable: '#1E7D56', active: '#1E7D56',
};
const STATUS_LABEL = {
    completed: 'Completed', administered: 'Administered', pending: 'Pending',
    scheduled: 'Upcoming', upcoming: 'Upcoming', overdue: 'Overdue',
    delayed: 'Delayed', missed: 'Missed', skipped: 'Skipped',
    alert: 'Alert', critical: 'Critical', stable: 'Stable', active: 'Active',
};
const getStatus = s => (s || 'pending').toLowerCase();

const STOCK_STATUS_STYLE = {
    'In Stock': { bg: '#e0faf4', color: '#0d6b4f' },
    'Low Stock': { bg: '#fff8e1', color: '#7c5a00' },
    'Out of Stock': { bg: '#fdecea', color: '#b71c1c' },
};

// Groups the existing Product categories into the three high-level filter
// buckets the Shared Stock page offers (Medicine / Food / Supplies).
// Anything not explicitly medication or food is treated as Supplies —
// this is display/filter-only and never changes stored category values.
const STOCK_CATEGORY_GROUP = {
    medication: 'Medicine',
    food: 'Food',
    medical_supplies: 'Supplies',
    hygiene: 'Supplies',
    General: 'Supplies',
    Cleaning: 'Supplies',
    Equipment: 'Supplies',
    'Linens & Bedding': 'Supplies',
};
const getStockCategoryGroup = (category) => STOCK_CATEGORY_GROUP[category] || 'Supplies';

const STOCK_REQUEST_STATUS_STYLE = {
    pending: { bg: '#fff8e1', color: '#7c5a00' },
    approved: { bg: '#e0faf4', color: '#0d6b4f' },
    rejected: { bg: '#fdecea', color: '#b71c1c' },
    fulfilled: { bg: '#e0faf4', color: '#0d6b4f' },
};
const requestStatusLabel = s => {
    const v = (s || 'pending');
    return v.charAt(0).toUpperCase() + v.slice(1);
};

const Badge = ({ s }) => (
    <span className={`status-badge ${getStatus(s)}`}>
        {STATUS_LABEL[getStatus(s)] || s}
    </span>
);

const DotBadge = ({ s }) => {
    const key = getStatus(s);
    return (
        <span className="dot-badge" style={{ color: STATUS_COLOR[key] || '#E65100' }}>
            <span className="dot" style={{ background: STATUS_COLOR[key] || '#E65100' }} />
            {STATUS_LABEL[key] || s}
        </span>
    );
};

// Builds a windowed page list with '…' gap markers so we never render
// dozens of number buttons in a row (e.g. 1 … 4 5 6 … 11 instead of 1-11).
const getPageRange = (current, total, siblings = 1) => {
    const totalNumbers = siblings * 2 + 5; // first, last, current, 2 siblings, 2 ellipses-as-slots
    if (total <= totalNumbers) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const left = Math.max(current - siblings, 1);
    const right = Math.min(current + siblings, total);
    const showLeftGap = left > 2;
    const showRightGap = right < total - 1;

    const range = [1];
    if (showLeftGap) range.push('gap-left');
    for (let p = left === 1 ? 2 : left; p <= (right === total ? total - 1 : right); p++) {
        if (p > 1 && p < total) range.push(p);
    }
    if (showRightGap) range.push('gap-right');
    if (total > 1) range.push(total);
    return range;
};

const Pagination = ({ page, pages, onChange }) => {
    if (pages <= 1) return null;
    return (
        <div className="res-pagination">
            <button
                type="button"
                className="page-num-btn page-nav-btn"
                disabled={page === 1}
                onClick={() => onChange(Math.max(1, page - 1))}
                aria-label="Previous page"
            >‹</button>
            {getPageRange(page, pages).map((n, i) => (
                typeof n === 'number' ? (
                    <button
                        key={n}
                        type="button"
                        className={`page-num-btn${page === n ? ' active' : ''}`}
                        aria-current={page === n ? 'page' : undefined}
                        onClick={() => onChange(n)}
                    >{n}</button>
                ) : (
                    <span key={n + i} className="page-ellipsis">…</span>
                )
            ))}
            <button
                type="button"
                className="page-num-btn page-nav-btn"
                disabled={page === pages}
                onClick={() => onChange(Math.min(pages, page + 1))}
                aria-label="Next page"
            >›</button>
        </div>
    );
};

const useFetch = () => useCallback(async (endpoint, opts = {}) => {
    const token = localStorage.getItem('token');
    // When uploading a file we pass a FormData body — the browser must set its
    // own multipart Content-Type (with boundary), so we skip the JSON default.
    const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
    try {
        const r = await fetch(`${API}${endpoint}`, {
            credentials: 'include',
            ...opts,
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                ...(token && { Authorization: `Bearer ${token}` }),
                ...opts.headers,
            },
        });
        const text = await r.text();
        const data = text ? JSON.parse(text) : {};
        return r.ok ? data : { success: false, message: data.message || data.error || `Request failed (${r.status})` };
    } catch (e) { return { success: false, message: e.message }; }
}, []);

const timeAgo = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const Toast = ({ msg, type, onDone }) => {
    useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
    return (
        <div className={`toast ${type || 'success'}`}>
            {type === 'error' ? <FaTimes /> : <FaCheck />} {msg}
        </div>
    );
};

const hcModalStyle = {
    maxWidth: 560, width: '100%', padding: 0,
    display: 'flex', flexDirection: 'column',
    maxHeight: 'min(92vh, 780px)', overflow: 'hidden', boxSizing: 'border-box',
};
const hcHeaderStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: 'clamp(16px,4vw,22px) clamp(18px,5vw,28px)', background: 'linear-gradient(135deg, #b85c2d, #7d3a06)',
    borderRadius: '20px 20px 0 0', flexShrink: 0, boxSizing: 'border-box', gap: 12,
};
const hcHeaderTitleStyle = {
    margin: 0, color: '#fff', fontFamily: "'Playfair Display', Georgia, serif",
    display: 'flex', alignItems: 'center', gap: 10, fontSize: 'clamp(1.02rem, 3vw, 1.2rem)',
    minWidth: 0, wordBreak: 'break-word',
};
const hcCloseBtnStyle = {
    background: 'rgba(255,255,255,.15)', border: '2px solid rgba(255,255,255,.2)',
    color: '#fff', width: 36, height: 36, borderRadius: '50%',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .2s', flexShrink: 0,
};
const hcBodyStyle = {
    padding: 'clamp(18px,4vw,26px) clamp(18px,5vw,28px)',
    flex: '1 1 auto', minHeight: 0, overflowY: 'auto', boxSizing: 'border-box',
};
const hcSectionLabel = {
    fontSize: '.78rem', fontWeight: 700, color: '#b85c2d', textTransform: 'uppercase',
    letterSpacing: '.05em', margin: '22px 0 14px', paddingBottom: 8,
    borderBottom: '1.5px solid #F3D9C4', display: 'flex', alignItems: 'center',
};
const hcFieldWrap = { marginBottom: 18 };
const hcGrid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 18 };

const FLOORS = ['2nd Floor', '3rd Floor', '4th Floor'];
const ROOM_NUMBERS = ['1', '2', '3', '4', '5', '6'];
const BEDS = ['Bed 1', 'Bed 2', 'Bed 3', 'Bed 4'];

const DOSAGE_UNITS = [
    'Tablet', 'Film-Coated Tablet', 'Caplet', 'Delayed-Release Tablet',
    'Delayed-Release Capsule', 'Enteric-Coated Tablet',
    'Capsule', 'Syrup', 'Chewable Tablet', 'Liquid Gel Capsule',
    'Controlled-Release Tablet', 'Oral Solution',
];
const guessDosageUnit = (form) => (DOSAGE_UNITS.includes(form) ? form : '');
const hcLabelStyle = {
    display: 'block', fontSize: '.82rem', fontWeight: 700, color: '#2c3e50',
    marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.04em',
};
const hcInputStyle = (hasError) => ({
    width: '100%', padding: '11px 14px',
    border: `1.5px solid ${hasError ? '#dc3545' : '#E8D6CC'}`,
    borderRadius: 10, fontSize: '.9rem',
    background: '#FFF8F3', color: '#1A0A00',
    outline: 'none', boxSizing: 'border-box',
    fontFamily: "'DM Sans', system-ui, sans-serif",
});
const hcErrorText = { color: '#dc3545', fontSize: '.78rem', marginTop: 4, display: 'block' };
const hcHintText = { fontSize: '.74rem', color: '#A38070', marginTop: 4, display: 'block' };
const hcFooter = {
    display: 'flex', flexWrap: 'wrap', gap: 12,
    padding: '18px clamp(18px,5vw,28px)', borderTop: '1.5px solid #E8D6CC',
    flexShrink: 0, boxSizing: 'border-box', background: '#fff',
};
const hcCancelBtn = (disabled) => ({
    flex: '1 1 120px', padding: '11px', background: '#fff', color: '#7A5C4E',
    border: '1.5px solid #E8D6CC', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, fontSize: '.9rem',
    transition: 'all .2s', opacity: disabled ? 0.6 : 1,
});
const hcSaveBtn = (disabled) => ({
    flex: '2 1 160px', padding: '11px',
    background: disabled ? '#ccc' : 'linear-gradient(135deg, #F96B38, #D94E1B)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 700, fontSize: '.9rem',
    boxShadow: disabled ? 'none' : '0 4px 14px rgba(249,107,56,.3)', transition: 'all .22s',
});

// ── Part 13: Schedule Type / recurring controls ──────────────────────────
// Kept visually consistent with the rest of the Add Medication modal
// (same palette, radius, fonts) rather than introducing new styling.
const hcSegWrap = {
    display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap',
};
const hcSegBtn = (active) => ({
    flex: '1 1 140px', padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
    border: `1.5px solid ${active ? '#D94E1B' : '#E8D6CC'}`,
    background: active ? 'linear-gradient(135deg, #F96B38, #D94E1B)' : '#FFF8F3',
    color: active ? '#fff' : '#7A5C4E',
    fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 700, fontSize: '.86rem',
    transition: 'all .2s',
});
const hcDayChip = (active) => ({
    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
    border: `1.5px solid ${active ? '#D94E1B' : '#E8D6CC'}`,
    background: active ? 'linear-gradient(135deg, #F96B38, #D94E1B)' : '#FFF8F3',
    color: active ? '#fff' : '#7A5C4E',
    fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, fontSize: '.78rem',
    transition: 'all .2s', textAlign: 'center',
});
const hcIconBtn = {
    width: 40, height: 40, flexShrink: 0, borderRadius: 10, cursor: 'pointer',
    border: '1.5px solid #E8D6CC', background: '#FFF8F3', color: '#b85c2d',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem',
};

const WEEKDAYS = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
];

// Never hardcoded — recomputed on every call so "past" always means past
// relative to the real current time/date, not a fixed value.
const pad2 = (n) => String(n).padStart(2, '0');
const nowLocalDateTimeValue = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const todayDateValue = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const HCField = ({ label, required, error, hint, style, children }) => (
    <div style={style || hcFieldWrap}>
        <label style={hcLabelStyle}>
            {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
        {children}
        {error && <small style={hcErrorText}>{error}</small>}
        {!error && hint && <small style={hcHintText}>{hint}</small>}
    </div>
);
const HCHeader = ({ icon, title, onClose }) => (
    <div style={hcHeaderStyle}>
        <h3 style={hcHeaderTitleStyle}>{icon} {title}</h3>
        <button onClick={onClose} type="button" style={hcCloseBtnStyle}><FaTimes /></button>
    </div>
);

const MEDICAL_CONDITIONS = [
    'Hypertension',
    'Type 2 Diabetes',
    'High Cholesterol',
    'Atrial Fibrillation / Stroke Prevention',
    'Cardiovascular Disease',
    'Arthritis',
    'Osteoporosis',
    'Gout',
    "Alzheimer's Disease / Dementia",
    "Parkinson's Disease",
    'Depression',
    'Anxiety',
    'Insomnia',
    'GERD / Heartburn',
    'Constipation',
    'Hypothyroidism',
    'Urinary Incontinence / Overactive Bladder',
    'Allergies',
    'Cold',
];

const ConditionsMultiSelect = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const toggleCondition = (condition) => {
        if (value.includes(condition)) {
            onChange(value.filter(c => c !== condition));
        } else {
            onChange([...value, condition]);
        }
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    ...hcInputStyle(false),
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', textAlign: 'left', gap: 10,
                }}
            >
                <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    color: value.length ? '#1A0A00' : '#A38070',
                }}>
                    {value.length ? value.join(', ') : 'Select medical condition(s)…'}
                </span>
                <FaChevronDown style={{ flexShrink: 0, fontSize: '.75rem', color: '#A38070', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 20,
                    background: '#fff', border: '1.5px solid #E8D6CC', borderRadius: 10,
                    boxShadow: '0 10px 28px rgba(0,0,0,.14)', maxHeight: 240, overflowY: 'auto',
                    padding: 8,
                }}>
                    {MEDICAL_CONDITIONS.map(condition => (
                        <label
                            key={condition}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                fontSize: '.87rem', color: '#1A0A00',
                                fontFamily: "'DM Sans', system-ui, sans-serif",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FFF8F3'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <input
                                type="checkbox"
                                checked={value.includes(condition)}
                                onChange={() => toggleCondition(condition)}
                                style={{ width: 16, height: 16, accentColor: '#F96B38', cursor: 'pointer', flexShrink: 0 }}
                            />
                            {condition}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

const AddResidentModal = ({ resident, onClose, onSaved, doFetch, toast, caregivers, fetchCaregivers }) => {
    const isEdit = !!resident;
    const [f, setF] = useState(() => isEdit ? {
        firstName: resident.firstName || '',
        lastName: resident.lastName || '',
        middleName: resident.middleName || '',
        nickname: resident.nickname || '',
        age: resident.age ?? '',
        dateOfBirth: resident.dateOfBirth ? new Date(resident.dateOfBirth).toISOString().slice(0, 10) : '',
        gender: resident.gender || 'female',
        roomNumber: resident.room || resident.roomNumber || '',
        floor: resident.floor || '',
        bed: resident.bed || '',
        location: resident.location || '',
        conditions: (resident.conditions || []).map(c => c?.name || c),
        primaryCaregiverId: (typeof resident.primaryCaregiverId === 'object'
            ? resident.primaryCaregiverId?._id
            : resident.primaryCaregiverId) || '',
        admissionDate: resident.admissionDate
            ? new Date(resident.admissionDate).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10),
        alertLevel: resident.alertLevel || 'stable',
    } : {
        firstName: '',
        lastName: '',
        middleName: '',
        nickname: '',
        age: '',
        dateOfBirth: '',
        gender: 'female',
        roomNumber: '',
        floor: '',
        bed: '',
        location: '',
        conditions: [],
        primaryCaregiverId: '',
        admissionDate: new Date().toISOString().slice(0, 10),
        alertLevel: 'stable',
    });
    const [errs, setErrs] = useState({});
    const [saving, setSaving] = useState(false);
    const [occupiedBeds, setOccupiedBeds] = useState([]);
    const setField = (k, v) => { setF(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

    const fileInputRef = useRef(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(isEdit ? (resident.photoUrl || '') : '');

    const pickPhoto = () => { if (!saving) fileInputRef.current?.click(); };

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast('Please choose an image file.', 'error'); return; }
        if (file.size > 5 * 1024 * 1024) { toast('Image must be smaller than 5MB.', 'error'); return; }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    useEffect(() => () => {
        if (photoPreview && photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    }, [photoPreview]);

    useEffect(() => {
        if (fetchCaregivers) fetchCaregivers();
    }, [fetchCaregivers]);

    useEffect(() => {
        if (!f.roomNumber) { setOccupiedBeds([]); return; }
        const params = new URLSearchParams({ roomNumber: f.roomNumber });
        if (f.floor) params.append('floor', f.floor);
        doFetch(`/head-caregiver/residents/occupied-beds?${params}`).then(r => {
            if (r.success) {

                const beds = isEdit
                    ? (r.data || []).filter(o => String(o.residentId) !== String(resident._id))
                    : (r.data || []);
                setOccupiedBeds(beds);
            }
        });
    }, [f.roomNumber, f.floor, doFetch, isEdit, resident]);

    const submit = async () => {
        const e = {};
        if (!f.nickname.trim()) e.nickname = 'Nickname is required';
        if (!f.age || isNaN(f.age) || +f.age < 1 || +f.age > 130) e.age = 'Enter a valid age (1–130)';
        if (!f.roomNumber.trim()) e.roomNumber = 'Room number is required';
        if (!f.floor) e.floor = 'Please select a floor';
        if (Object.keys(e).length) { setErrs(e); return; }

        setSaving(true);

        const selectedCaregiver = caregivers.find(c => String(c._id) === String(f.primaryCaregiverId));

        const payload = {
            firstName: f.firstName.trim(),
            lastName: f.lastName.trim(),
            middleName: f.middleName.trim(),
            nickname: f.nickname.trim(),
            age: +f.age,
            dateOfBirth: f.dateOfBirth || null,
            gender: f.gender,
            roomNumber: f.roomNumber.trim(),
            floor: f.floor,
            bed: f.bed,
            location: f.location.trim(),
            alertLevel: f.alertLevel,
            admissionDate: f.admissionDate,
            conditions: f.conditions.map(c => ({ name: c })),
            primaryCaregiverId: f.primaryCaregiverId || '',
            primaryCaregiver: selectedCaregiver ? `${selectedCaregiver.firstName} ${selectedCaregiver.lastName}` : '',
            primaryCaregiverName: selectedCaregiver ? `${selectedCaregiver.firstName} ${selectedCaregiver.lastName}` : '',
        };

        const r = isEdit
            ? await doFetch(`/head-caregiver/residents/${resident._id}`, { method: 'PUT', body: JSON.stringify(payload) })
            : await doFetch('/head-caregiver/residents', { method: 'POST', body: JSON.stringify(payload) });

        if (!r.success) {
            setSaving(false);
            toast(r.message || (isEdit ? 'Failed to update resident.' : 'Failed to add resident.'), 'error');
            return;
        }

        let savedResident = r.data;
        const displayName = (f.firstName || f.nickname || 'Resident');

        if (photoFile && savedResident?._id) {
            const body = new FormData();
            body.append('photo', photoFile);
            const photoR = await doFetch(`/residents/${savedResident._id}/photo`, {
                method: 'PUT',
                body
            });
            if (photoR.success) {
                savedResident = { ...savedResident, photoUrl: photoR.data?.photoUrl || photoR.photoUrl };
            } else {
                toast(photoR.message || 'Resident saved, but the photo failed to upload.', 'error');
            }
        }

        setSaving(false);
        toast(isEdit
            ? `Resident ${displayName} ${f.lastName}`.trim() + ' updated successfully.'
            : `Resident ${displayName} ${f.lastName}`.trim() + ' added successfully.');
        onSaved(savedResident);
        onClose();
    };

    const selectedCaregiver = caregivers.find(c => String(c._id) === String(f.primaryCaregiverId));
    const selectedCaregiverName = selectedCaregiver ? `${selectedCaregiver.firstName} ${selectedCaregiver.lastName}` : '';

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaUserPlus />} title={isEdit ? `Edit Resident — ${resident.firstName} ${resident.lastName}`.trim() : 'Add New Resident'} onClose={onClose} />
                <div style={hcBodyStyle}>

                    <div className="add-resident-photo-row">
                        <div className="add-resident-avatar-wrap">
                            <div
                                className={`add-resident-avatar${photoPreview ? '' : ' add-resident-avatar-empty'}`}
                                onClick={pickPhoto}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') pickPhoto(); }}
                            >
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Resident" className="add-resident-avatar-img" />
                                ) : (
                                    <FaCamera className="add-resident-avatar-placeholder-icon" />
                                )}
                            </div>
                            <button
                                type="button"
                                className="add-resident-avatar-upload-btn"
                                onClick={pickPhoto}
                                disabled={saving}
                                title="Add photo"
                            >
                                <FaCamera />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                        <div className="add-resident-photo-hint">
                            {photoPreview ? (
                                <>Change profile photo <span className="add-resident-photo-optional">(optional)</span></>
                            ) : (
                                <>Click to upload profile photo <span className="add-resident-photo-optional">(optional)</span></>
                            )}
                        </div>
                    </div>

                    <div style={{ ...hcSectionLabel, marginTop: 0 }}>
                        <FaUserCircle style={{ marginRight: 6 }} /> Personal Information
                    </div>
                    <div style={hcGrid2}>
                        <HCField label="First Name">
                            <input
                                style={hcInputStyle(false)}
                                value={f.firstName}
                                onChange={e => setField('firstName', e.target.value)}
                                placeholder="Enter first name (optional)"
                            />
                        </HCField>
                        <HCField label="Last Name">
                            <input
                                style={hcInputStyle(false)}
                                value={f.lastName}
                                onChange={e => setField('lastName', e.target.value)}
                                placeholder="Enter last name (optional)"
                            />
                        </HCField>
                        <HCField label="Middle Name">
                            <input
                                style={hcInputStyle(false)}
                                value={f.middleName}
                                onChange={e => setField('middleName', e.target.value)}
                                placeholder="Optional"
                            />
                        </HCField>
                        <HCField label="Nickname" required error={errs.nickname}>
                            <input
                                style={hcInputStyle(errs.nickname)}
                                value={f.nickname}
                                onChange={e => setField('nickname', e.target.value)}
                                placeholder="What they prefer to be called"
                            />
                        </HCField>
                        <HCField label="Age" required error={errs.age}>
                            <input
                                type="number"
                                min="1"
                                max="130"
                                style={hcInputStyle(errs.age)}
                                value={f.age}
                                onChange={e => setField('age', e.target.value)}
                                placeholder="e.g. 75"
                            />
                        </HCField>
                        <HCField label="Gender">
                            <select style={hcInputStyle(false)} value={f.gender} onChange={e => setField('gender', e.target.value)}>
                                <option value="female">Female</option>
                                <option value="other">Other / Prefer not to say</option>
                            </select>
                        </HCField>
                        <HCField label="Date of Birth" style={{ ...hcFieldWrap, gridColumn: '1 / -1' }}>
                            <input
                                type="date"
                                style={hcInputStyle(false)}
                                value={f.dateOfBirth}
                                onChange={e => setField('dateOfBirth', e.target.value)}
                            />
                        </HCField>
                        <HCField label="Admission Date" style={{ ...hcFieldWrap, gridColumn: '1 / -1' }}>
                            <input
                                type="date"
                                style={hcInputStyle(false)}
                                value={f.admissionDate}
                                onChange={e => setField('admissionDate', e.target.value)}
                            />
                        </HCField>
                        <HCField label="Location" style={{ ...hcFieldWrap, gridColumn: '1 / -1' }} hint="Where the resident was picked up from or found">
                            <input
                                style={hcInputStyle(false)}
                                value={f.location}
                                onChange={e => setField('location', e.target.value)}
                                placeholder="e.g. Barangay Hall, Riverside area (optional)"
                            />
                        </HCField>
                    </div>

                    <div style={hcSectionLabel}>
                        <FaHome style={{ marginRight: 6 }} /> Room Assignment
                    </div>
                    <div style={hcGrid2}>
                        <HCField label="Room Number" required error={errs.roomNumber}>
                            <select
                                style={hcInputStyle(errs.roomNumber)}
                                value={f.roomNumber}
                                onChange={e => setField('roomNumber', e.target.value)}>
                                <option value="">Select room…</option>
                                {ROOM_NUMBERS.map(rn => <option key={rn} value={rn}>Room {rn}</option>)}
                            </select>
                        </HCField>
                        <HCField label="Floor / Ward" required error={errs.floor}>
                            <select
                                style={hcInputStyle(errs.floor)}
                                value={f.floor}
                                onChange={e => setField('floor', e.target.value)}>
                                <option value="">Select floor…</option>
                                {FLOORS.map(fl => <option key={fl} value={fl}>{fl}</option>)}
                            </select>
                        </HCField>
                        <HCField
                            label="Bed"
                            error={errs.bed}
                            hint={f.roomNumber && occupiedBeds.length > 0
                                ? `${occupiedBeds.length} of ${BEDS.length} bed${occupiedBeds.length !== 1 ? 's' : ''} occupied in Room ${f.roomNumber}`
                                : null}
                        >
                            <select style={hcInputStyle(errs.bed)} value={f.bed} onChange={e => setField('bed', e.target.value)}>
                                <option value="">Select bed…</option>
                                {BEDS.map(b => {
                                    const occ = occupiedBeds.find(o => o.bed === b);
                                    return (
                                        <option key={b} value={b} disabled={!!occ}>
                                            {b}{occ ? ` — Occupied (${occ.residentName})` : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </HCField>
                        <HCField label="Alert Level">
                            <select style={hcInputStyle(false)} value={f.alertLevel} onChange={e => setField('alertLevel', e.target.value)}>
                                <option value="stable">Stable</option>
                                <option value="alert">Alert - Monitor closely</option>
                                <option value="critical">Critical - Immediate attention</option>
                            </select>
                        </HCField>
                    </div>

                    <div style={hcSectionLabel}>
                        <FaStethoscope style={{ marginRight: 6 }} /> Medical Information
                    </div>
                    <HCField label="Medical Conditions" hint="Select one or more conditions">
                        <ConditionsMultiSelect
                            value={f.conditions}
                            onChange={vals => setField('conditions', vals)}
                        />
                    </HCField>

                    <div style={hcSectionLabel}>
                        <FaUserMd style={{ marginRight: 6 }} /> Assignment
                    </div>
                    <HCField
                        label="Primary Caregiver"
                        style={{ marginBottom: 6 }}
                        hint={
                            selectedCaregiverName ? `Assigned to: ${selectedCaregiverName}`
                                : caregivers.filter(c => String(c.role || '').toLowerCase() === 'caregiver').length === 0
                                    ? 'No caregiver accounts found. Please register a caregiver first in User Management.'
                                    : 'Optional — can be assigned later.'
                        }
                    >
                        <select
                            style={hcInputStyle(false)}
                            value={f.primaryCaregiverId}
                            onChange={e => setField('primaryCaregiverId', e.target.value)}>
                            <option value="">— Select a caregiver —</option>
                            {caregivers
                                .filter(c => String(c.role || '').toLowerCase() === 'caregiver')
                                .map(c => (
                                    <option key={c._id} value={c._id}>
                                        {c.firstName} {c.lastName} (Caregiver)
                                    </option>
                                ))}
                        </select>
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>
                        Cancel
                    </button>
                    <button onClick={submit} type="button" disabled={saving} style={hcSaveBtn(saving)}>
                        {saving ? 'Saving…' : isEdit ? '✓ Save Changes' : '✓ Add Resident'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DISCHARGE_REASON_OPTIONS = [
    { value: '', label: 'Select a reason…' },
    { value: 'deceased', label: 'Death' },
    { value: 'legal_guardianship', label: 'Legal Guardianship Change' },
    { value: 'adopted', label: 'Adopted' },
    { value: 'reunited_with_family', label: 'Reunited with Family' },
    { value: 'transferred_hospital', label: 'Transferred to Hospital (end-of-life care)' },
    { value: 'other', label: 'Other' },
];

const DischargeResidentModal = ({ resident, onClose, onSaved, doFetch, toast }) => {
    const [reason, setReason] = useState('');
    const [causeOfDeath, setCauseOfDeath] = useState('');
    const [destination, setDestination] = useState('');
    const [notes, setNotes] = useState('');
    const [errs, setErrs] = useState({});
    const [saving, setSaving] = useState(false);

    const resName = resident.name || [resident.firstName, resident.lastName].filter(Boolean).join(' ') || 'this resident';

    const showDestination = ['legal_guardianship', 'adopted', 'reunited_with_family', 'transferred_hospital', 'other'].includes(reason);
    const destinationLabel = {
        legal_guardianship: 'New Legal Guardian',
        adopted: 'Adoptive Family / Guardian',
        reunited_with_family: 'Receiving Family Member',
        transferred_hospital: 'Hospital Name',
        other: 'Please Specify',
    }[reason] || 'Destination';

    const submit = async () => {
        const e = {};
        if (!reason) e.reason = 'Please select a reason.';
        if (reason === 'deceased' && !causeOfDeath.trim()) e.causeOfDeath = 'Cause of death is required.';
        if (showDestination && !destination.trim()) e.destination = `${destinationLabel} is required.`;
        if (Object.keys(e).length) { setErrs(e); return; }

        setSaving(true);
        const r = await doFetch(`/head-caregiver/residents/${resident._id}/discharge`, {
            method: 'PUT',
            body: JSON.stringify({
                reason,
                causeOfDeath: reason === 'deceased' ? causeOfDeath.trim() : undefined,
                destination: showDestination ? destination.trim() : undefined,
                notes: notes.trim(),
            })
        });
        setSaving(false);
        if (r.success) {
            toast(r.message || `${resName} has been removed from active residents.`);
            onSaved(resident._id);
            onClose();
        } else {
            toast(r.message || 'Failed to remove resident.', 'error');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaExclamationCircle />} title={`Remove Resident — ${resName}`} onClose={onClose} />
                <div style={hcBodyStyle}>
                    <p style={{ color: 'var(--d-muted)', fontSize: '.85rem', marginTop: 0 }}>
                        This does not delete {resName}'s record — it keeps their medication and care
                        history on file and moves them out of the active resident list.
                    </p>

                    <HCField label="Reason for removal" required error={errs.reason}>
                        <select
                            style={hcInputStyle(errs.reason)}
                            value={reason}
                            onChange={e => { setReason(e.target.value); setErrs(p => ({ ...p, reason: '' })); }}
                        >
                            {DISCHARGE_REASON_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </HCField>

                    {reason === 'deceased' && (
                        <HCField label="Cause of Death" required error={errs.causeOfDeath}>
                            <input
                                style={hcInputStyle(errs.causeOfDeath)}
                                value={causeOfDeath}
                                onChange={e => { setCauseOfDeath(e.target.value); setErrs(p => ({ ...p, causeOfDeath: '' })); }}
                                placeholder="e.g. Cardiac arrest, natural causes, etc."
                            />
                        </HCField>
                    )}

                    {showDestination && (
                        <HCField label={destinationLabel} required error={errs.destination}>
                            <input
                                style={hcInputStyle(errs.destination)}
                                value={destination}
                                onChange={e => { setDestination(e.target.value); setErrs(p => ({ ...p, destination: '' })); }}
                                placeholder={`Enter ${destinationLabel.toLowerCase()}`}
                            />
                        </HCField>
                    )}

                    <HCField label="Additional Notes">
                        <textarea
                            style={{ ...hcInputStyle(false), minHeight: 70, resize: 'vertical' }}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Optional — any additional details for the record"
                        />
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        type="button"
                        disabled={saving}
                        style={{ ...hcSaveBtn(saving), background: saving ? undefined : '#C0392B' }}
                    >
                        {saving ? 'Removing…' : 'Confirm Removal'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DeleteMedicationModal = ({ log, onClose, onSaved, doFetch, toast }) => {
    const [saving, setSaving] = useState(false);
    const medName = log?.medicationName || 'this medication';
    const resName = log?.residentName || 'this resident';

    const submit = async () => {
        setSaving(true);
        const r = await doFetch(`/head-caregiver/schedule/${log._id}`, { method: 'DELETE' });
        setSaving(false);
        if (r.success) {
            toast(r.message || `${medName} removed for ${resName}.`);
            onSaved(log._id);
            onClose();
        } else {
            toast(r.message || 'Failed to delete medication.', 'error');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={{ ...hcModalStyle, maxWidth: 440 }}>
                <HCHeader icon={<FaTrashAlt />} title="Delete Medication" onClose={onClose} />
                <div style={hcBodyStyle}>
                    <p style={{ color: 'var(--d-muted)', fontSize: '.88rem', marginTop: 0 }}>
                        Are you sure you want to delete <strong>{medName}</strong> for <strong>{resName}</strong>?
                        This removes it from the schedule and medication history and cannot be undone.
                    </p>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        type="button"
                        disabled={saving}
                        style={{ ...hcSaveBtn(saving), background: saving ? undefined : '#C0392B' }}
                    >
                        {saving ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AssignCaregiverModal = ({ resident, caregivers, onClose, onSaved, doFetch, toast, fetchCaregivers }) => {
    const currentCaregiverId = typeof resident.primaryCaregiverId === 'object'
        ? resident.primaryCaregiverId?._id
        : resident.primaryCaregiverId;
    const [caregiverId, setCaregiverId] = useState(currentCaregiverId || '');
    const [saving, setSaving] = useState(false);
    const residentName = resident.name || [resident.firstName, resident.lastName].filter(Boolean).join(' ') || 'Resident';
    const availableCaregivers = caregivers.filter(c => String(c.role || '').toLowerCase() === 'caregiver');

    useEffect(() => {
        if (fetchCaregivers) fetchCaregivers();
    }, [fetchCaregivers]);

    const submit = async () => {
        if (!caregiverId) {
            toast('Please select a caregiver.', 'error');
            return;
        }

        setSaving(true);
        const r = await doFetch(`/head-caregiver/residents/${resident._id}/assign-caregiver`, {
            method: 'PUT',
            body: JSON.stringify({ caregiverId })
        });
        setSaving(false);

        if (r.success) {
            onSaved(r.data);
            toast(r.message || `Caregiver assigned to ${residentName}.`);
            onClose();
        } else {
            toast(r.message || 'Failed to assign caregiver.', 'error');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaUserMd />} title={`Assign Caregiver - ${residentName}`} onClose={onClose} />
                <div style={hcBodyStyle}>
                    <HCField label="Current Caregiver">
                        <input
                            style={hcInputStyle(false)}
                            value={resident.primaryCaregiverName || resident.primaryCaregiver || 'Unassigned'}
                            disabled
                        />
                    </HCField>
                    <HCField label="Caregiver" required
                        hint={availableCaregivers.length === 0 ? 'No caregiver accounts found. Add a caregiver in admin first.' : undefined}>
                        <select style={hcInputStyle(false)} value={caregiverId} onChange={e => setCaregiverId(e.target.value)}>
                            <option value="">Select a caregiver</option>
                            {availableCaregivers.map(c => (
                                <option key={c._id} value={c._id}>
                                    {c.firstName} {c.lastName} ({c.staffId || 'Caregiver'})
                                </option>
                            ))}
                        </select>
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>Cancel</button>
                    <button
                        onClick={submit}
                        type="button"
                        disabled={saving || !caregiverId || availableCaregivers.length === 0}
                        style={hcSaveBtn(saving || !caregiverId || availableCaregivers.length === 0)}
                    >
                        {saving ? 'Saving…' : 'Assign Caregiver'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProfileModal = ({ resident, schedule, onClose, onSaved, doFetch, toast, onDuty }) => {
    const resName = resident.name || [resident.firstName, resident.lastName].filter(Boolean).join(' ') || 'Resident';
    const todayMeds = (schedule || []).filter(l =>
        l.residentName === resName || l.residentId?.toString() === resident._id?.toString()
    );

    const fileInputRef = useRef(null);
    const [photoUrl, setPhotoUrl] = useState(resident.photoUrl || '');
    const [preview, setPreview] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const pickPhoto = () => { if (!uploading && onDuty) fileInputRef.current?.click(); };

    // Selecting a photo only stages it locally (preview + pending file).
    // It is NOT sent to the backend until "Save Edits" is clicked.
    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast('Please choose an image file.', 'error');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast('Image must be smaller than 5MB.', 'error');
            return;
        }

        if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
        setPreview(URL.createObjectURL(file));
        setPhotoFile(file);
    };

    const discardPhotoEdit = () => {
        if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
        setPreview('');
        setPhotoFile(null);
    };

    useEffect(() => () => {
        if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    }, [preview]);

    const saveEdits = async () => {
        if (!photoFile) return;
        setUploading(true);

        const body = new FormData();
        body.append('photo', photoFile);
        const r = await doFetch(`/residents/${resident._id}/photo`, {
            method: 'PUT',
            body
        });

        setUploading(false);

        if (r.success) {
            const newUrl = r.data?.photoUrl || r.photoUrl;
            setPhotoUrl(newUrl);
            if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
            setPreview('');
            setPhotoFile(null);
            toast(r.message || 'Photo updated.');
            onSaved?.({ _id: resident._id, photoUrl: newUrl });
        } else {
            toast(r.message || 'Failed to upload photo.', 'error');
        }
    };

    const InfoRow = ({ label, value }) => value ? (
        <div className="profile-info-row">
            <span className="profile-info-label">{label}</span>
            <span className="profile-info-value">{value}</span>
        </div>
    ) : null;

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaUserCircle />} title={`Resident Profile — ${resName}`} onClose={onClose} />
                <div style={{ ...hcBodyStyle, padding: 0 }}>
                    <div className="profile-header-card">
                        <div className="profile-avatar-wrap">
                            <div className="profile-avatar">
                                {(preview || photoUrl) ? (
                                    <img src={preview || photoUrl} alt={resName} className="profile-avatar-img" />
                                ) : (
                                    <span className="profile-avatar-placeholder"><FaUserCircle /></span>
                                )}
                                {uploading && <div className="profile-avatar-uploading"><FaSpinner className="spin" /></div>}
                            </div>
                            <button
                                type="button"
                                className="profile-avatar-upload-btn"
                                onClick={pickPhoto}
                                disabled={uploading || !onDuty}
                                title={onDuty ? "Change photo" : "Not available while off duty"}
                            >
                                <FaCamera />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                        <div className="profile-header-info">
                            <h3 className="profile-full-name">{resName}</h3>
                            {resident.nickname && (
                                <div className="profile-nickname">aka "{resident.nickname}"</div>
                            )}
                            <div className="profile-header-meta">
                                <span>Age: {resident.age || '—'}</span>
                                <span>·</span>
                                <span>{resident.gender ? resident.gender.charAt(0).toUpperCase() + resident.gender.slice(1) : '—'}</span>
                                <span>·</span>
                                <span>Room {resident.room || '—'}</span>
                            </div>
                            <Badge s={resident.alertLevel || 'stable'} />
                        </div>
                    </div>

                    <div className="profile-grid">
                        <div className="profile-section">
                            <div className="profile-section-title">Personal Information</div>
                            <InfoRow label="First Name" value={resident.firstName} />
                            <InfoRow label="Middle Name" value={resident.middleName} />
                            <InfoRow label="Last Name" value={resident.lastName} />
                            <InfoRow label="Nickname" value={resident.nickname} />
                            <InfoRow label="Age" value={resident.age} />
                            <InfoRow label="Date of Birth" value={resident.dateOfBirth ? new Date(resident.dateOfBirth).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not provided'} />
                            <InfoRow label="Gender" value={resident.gender} />
                            <InfoRow label="Admission Date" value={resident.admissionDate ? new Date(resident.admissionDate).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' }) : null} />
                            <InfoRow label="Location" value={resident.location} />
                        </div>

                        <div className="profile-section">
                            <div className="profile-section-title">Room Assignment</div>
                            <InfoRow label="Room Number" value={resident.room} />
                            <InfoRow label="Floor / Ward" value={resident.floor} />
                            <InfoRow label="Bed" value={resident.bed} />
                            <InfoRow label="Alert Level" value={resident.alertLevel ? resident.alertLevel.charAt(0).toUpperCase() + resident.alertLevel.slice(1) : null} />
                        </div>

                        <div className="profile-section profile-section-full">
                            <div className="profile-section-title">Medical Conditions</div>
                            {resident.conditions?.length > 0 ? (
                                <div className="conditions-wrap" style={{ marginTop: 6 }}>
                                    {resident.conditions.map((c, i) => (
                                        <span key={i} className="condition-tag">{c?.name || c}</span>
                                    ))}
                                </div>
                            ) : (
                                <span className="profile-info-value" style={{ color: 'var(--d-muted)', fontStyle: 'italic' }}>No conditions recorded.</span>
                            )}
                        </div>

                        <div className="profile-section profile-section-full">
                            <div className="profile-section-title">Assigned Personnel</div>
                            <InfoRow label="Primary Caregiver" value={resident.primaryCaregiverName || resident.primaryCaregiver || '—'} />
                        </div>
                    </div>

                    <div className="profile-meds-section">
                        <div className="profile-section-title" style={{ marginTop: 16, marginBottom: 10 }}>Today's Medication Schedule</div>
                        {todayMeds.length === 0 ? (
                            <p style={{ color: 'var(--d-muted)', fontStyle: 'italic', fontSize: '.88rem', margin: 0 }}>No medications scheduled today.</p>
                        ) : (
                            <div className="table-scroll">
                                <table className="custom-table">
                                    <thead>
                                        <tr><th>Time</th><th>Medication</th><th>Dosage</th><th>Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {todayMeds.map(m => (
                                            <tr key={m._id}>
                                                <td>{m.scheduledTime ? new Date(m.scheduledTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                                <td><strong>{m.medicationName || '—'}</strong></td>
                                                <td>{m.dosage || '—'}</td>
                                                <td><Badge s={m.status} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ ...hcFooter, justifyContent: 'flex-end' }}>
                    {photoFile ? (
                        <>
                            <button onClick={discardPhotoEdit} type="button" disabled={uploading} style={{ ...hcCancelBtn(uploading), flex: '0 1 140px' }}>Discard</button>
                            <button onClick={saveEdits} type="button" disabled={uploading} style={{ ...hcSaveBtn(uploading), flex: '0 1 160px' }}>
                                {uploading ? 'Saving…' : 'Save Edits'}
                            </button>
                        </>
                    ) : (
                        <button onClick={onClose} type="button" style={{ ...hcCancelBtn(false), flex: '0 1 140px' }}>Close</button>
                    )}
                </div>
            </div>
        </div>
    );
};

const HistoryModal = ({ resident, onClose, doFetch }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            const r = await doFetch(`/head-caregiver/residents/${resident._id}/medication-history`);
            setLogs(r.success ? (r.data || []) : []);
            setLoading(false);
        })();
    }, [resident._id, doFetch]);

    const resName = resident.name || [resident.firstName, resident.lastName].filter(Boolean).join(' ') || 'Resident';

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={{ ...hcModalStyle, maxWidth: 860 }}>
                <HCHeader icon={<FaEye />} title={`Medication History — ${resName}`} onClose={onClose} />
                <div style={hcBodyStyle}>
                    {loading ? <div className="no-data-center"><FaSpinner className="spin" /> Loading…</div>
                        : logs.length === 0 ? <div className="no-data-center">No medication history found.</div>
                            : <div className="history-scroll">
                                <table className="custom-table">
                                    <thead><tr><th>Date &amp; Time</th><th>Medication</th><th>Dosage</th><th>Status</th><th>Notes</th></tr></thead>
                                    <tbody>
                                        {logs.map(l => (
                                            <tr key={l._id}>
                                                <td>{l.scheduledTime ? new Date(l.scheduledTime).toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                                <td><strong>{l.medicationName || '—'}</strong></td>
                                                <td>{l.dosage || '—'}</td>
                                                <td><Badge s={l.status} /></td>
                                                <td className="td-muted">{l.notes || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>}
                </div>
                <div style={{ ...hcFooter, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} type="button" style={{ ...hcCancelBtn(false), flex: '0 1 140px' }}>Close</button>
                </div>
            </div>
        </div>
    );
};

const AddScheduleModal = ({ residents, medications, onClose, onSaved, doFetch, toast, defaultResident }) => {
    const [f, setF] = useState({
        residentId: defaultResident?._id || '', medicationId: '',
        scheduleType: 'one_time', // Part 13: One Time is the default
        scheduledTime: '',
        dosageAmount: '', dosageUnit: '', notes: '',
        // Recurring-only fields (Part 13):
        startDate: '', duration: '7_days', endDate: '',
        repeat: 'every_day', selectedDays: [], times: [''],
    });
    const [errs, setErrs] = useState({});
    const [saving, setSaving] = useState(false);
    const setField = (k, v) => { setF(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

    const pickMedication = (medicationId) => {
        const med = medications.find(m => m._id === medicationId);
        setF(p => ({ ...p, medicationId, dosageUnit: guessDosageUnit(med?.form) || p.dosageUnit }));
        setErrs(p => ({ ...p, medicationId: '' }));
    };

    const isRecurring = f.scheduleType === 'recurring';
    const toggleDay = (key) => {
        setF(p => ({
            ...p,
            selectedDays: p.selectedDays.includes(key)
                ? p.selectedDays.filter(d => d !== key)
                : [...p.selectedDays, key],
        }));
        setErrs(p => ({ ...p, selectedDays: '' }));
    };
    const setTimeAt = (i, v) => {
        setF(p => ({ ...p, times: p.times.map((t, idx) => idx === i ? v : t) }));
        setErrs(p => ({ ...p, times: '' }));
    };
    const addTime = () => setF(p => ({ ...p, times: [...p.times, ''] }));
    const removeTime = (i) => setF(p => ({ ...p, times: p.times.length > 1 ? p.times.filter((_, idx) => idx !== i) : p.times }));

    const submit = async () => {
        const e = {};
        if (!f.residentId) e.residentId = 'Select a resident';
        if (!f.medicationId) e.medicationId = 'Select a medication';
        if (!f.dosageAmount) e.dosageAmount = 'Enter an amount';
        if (!f.dosageUnit) e.dosageUnit = 'Select a unit';

        if (!isRecurring) {
            if (!f.scheduledTime) e.scheduledTime = 'Select date & time';
            if (f.scheduledTime && new Date(f.scheduledTime) < new Date(Date.now() - 60000)) {
                e.scheduledTime = 'Scheduled time cannot be in the past.';
            }
        } else {
            if (!f.startDate) e.startDate = 'Select a start date';
            if (f.startDate && f.startDate < todayDateValue()) e.startDate = 'Start date cannot be in the past.';
            if (f.duration === 'custom') {
                if (!f.endDate) e.endDate = 'Select an end date';
                else if (f.endDate < f.startDate) e.endDate = 'End date cannot be before the start date.';
            }
            if (f.repeat === 'selected_days' && f.selectedDays.length === 0) {
                e.selectedDays = 'Select at least one day';
            }
            const cleanTimes = f.times.map(t => t.trim()).filter(Boolean);
            if (!cleanTimes.length) e.times = 'Add at least one time';
        }

        if (Object.keys(e).length) { setErrs(e); return; }
        setSaving(true);

        const dosage = f.dosageAmount && f.dosageUnit ? `${f.dosageAmount} ${f.dosageUnit}` : '';
        const payload = isRecurring
            ? {
                residentId: f.residentId,
                medicationId: f.medicationId,
                scheduleType: 'recurring',
                dosage,
                notes: f.notes,
                startDate: f.startDate,
                duration: f.duration,
                endDate: f.duration === 'custom' ? f.endDate : undefined,
                repeat: f.repeat,
                selectedDays: f.repeat === 'selected_days' ? f.selectedDays : undefined,
                times: f.times.map(t => t.trim()).filter(Boolean),
            }
            : {
                residentId: f.residentId,
                medicationId: f.medicationId,
                scheduleType: 'one_time',
                scheduledTime: f.scheduledTime,
                dosage,
                notes: f.notes,
            };

        const r = await doFetch('/head-caregiver/schedule', { method: 'POST', body: JSON.stringify(payload) });
        setSaving(false);
        if (r.success) {
            toast(isRecurring ? (r.message || `${r.count || (Array.isArray(r.data) ? r.data.length : 1)} doses scheduled.`) : 'Medication scheduled.');
            onSaved(r.data);
            onClose();
        } else toast(r.message || 'Failed.', 'error');
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={{ ...hcModalStyle, maxWidth: 660 }}>
                <HCHeader icon={<FaPlus />} title="Add Medication to Schedule" onClose={onClose} />
                <div style={hcBodyStyle}>
                    <HCField label="Resident" required error={errs.residentId}>
                        <select style={hcInputStyle(errs.residentId)} value={f.residentId} onChange={e => setField('residentId', e.target.value)}>
                            <option value="">Select resident…</option>
                            {residents.map(r => {
                                const displayName = r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim();
                                return <option key={r._id} value={r._id}>{displayName} — Room {r.room || '?'}</option>;
                            })}
                        </select>
                    </HCField>
                    <HCField label="Medication" required error={errs.medicationId}>
                        <select style={hcInputStyle(errs.medicationId)} value={f.medicationId} onChange={e => pickMedication(e.target.value)}>
                            <option value="">Select medication…</option>
                            {medications.map(m => <option key={m._id} value={m._id}>{m.name} {m.dosage?.value ? `${m.dosage.value}${m.dosage.unit}` : ''}</option>)}
                        </select>
                    </HCField>

                    <HCField label="Schedule Type" required>
                        <div style={hcSegWrap}>
                            <div style={hcSegBtn(!isRecurring)} onClick={() => setField('scheduleType', 'one_time')}>One Time</div>
                            <div style={hcSegBtn(isRecurring)} onClick={() => setField('scheduleType', 'recurring')}>Recurring</div>
                        </div>
                    </HCField>

                    {!isRecurring && (
                        <div style={hcGrid2}>
                            <HCField label="Scheduled Date & Time" required error={errs.scheduledTime}>
                                <input
                                    type="datetime-local"
                                    min={nowLocalDateTimeValue()}
                                    style={hcInputStyle(errs.scheduledTime)}
                                    value={f.scheduledTime}
                                    onChange={e => setField('scheduledTime', e.target.value)} />
                            </HCField>
                            <HCField label="Dosage Override" required error={errs.dosageAmount || errs.dosageUnit}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        step="any"
                                        style={{ ...hcInputStyle(errs.dosageAmount), flex: '0 0 35%' }}
                                        value={f.dosageAmount}
                                        onChange={e => setField('dosageAmount', e.target.value)}
                                        placeholder="1" />
                                    <div
                                        title="Detected automatically from the selected medication's form — not editable"
                                        style={{
                                            ...hcInputStyle(errs.dosageUnit),
                                            flex: '1 1 65%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            background: '#F0EAE4',
                                            color: f.dosageUnit ? '#1A0A00' : '#A38070',
                                            cursor: 'not-allowed',
                                        }}>
                                        {f.dosageUnit || (f.medicationId ? 'Form not recognized — contact admin' : 'Select a medication first')}
                                    </div>
                                </div>
                            </HCField>
                        </div>
                    )}

                    {isRecurring && (
                        <>
                            <div style={hcGrid2}>
                                <HCField label="Start Date" required error={errs.startDate}>
                                    <input
                                        type="date"
                                        min={todayDateValue()}
                                        style={hcInputStyle(errs.startDate)}
                                        value={f.startDate}
                                        onChange={e => setField('startDate', e.target.value)} />
                                </HCField>
                                <HCField label="Dosage Override" required error={errs.dosageAmount || errs.dosageUnit}>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0"
                                            step="any"
                                            style={{ ...hcInputStyle(errs.dosageAmount), flex: '0 0 35%' }}
                                            value={f.dosageAmount}
                                            onChange={e => setField('dosageAmount', e.target.value)}
                                            placeholder="1" />
                                        <div
                                            title="Detected automatically from the selected medication's form — not editable"
                                            style={{
                                                ...hcInputStyle(errs.dosageUnit),
                                                flex: '1 1 65%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                background: '#F0EAE4',
                                                color: f.dosageUnit ? '#1A0A00' : '#A38070',
                                                cursor: 'not-allowed',
                                            }}>
                                            {f.dosageUnit || (f.medicationId ? 'Form not recognized — contact admin' : 'Select a medication first')}
                                        </div>
                                    </div>
                                </HCField>
                            </div>

                            <div style={hcGrid2}>
                                <HCField label="Duration" required>
                                    <select style={hcInputStyle(false)} value={f.duration} onChange={e => setField('duration', e.target.value)}>
                                        <option value="7_days">7 Days</option>
                                        <option value="2_weeks">2 Weeks</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </HCField>
                                {f.duration === 'custom' ? (
                                    <HCField label="End Date" required error={errs.endDate}>
                                        <input
                                            type="date"
                                            min={f.startDate || todayDateValue()}
                                            style={hcInputStyle(errs.endDate)}
                                            value={f.endDate}
                                            onChange={e => setField('endDate', e.target.value)} />
                                    </HCField>
                                ) : (
                                    <HCField label="Repeat" required>
                                        <select style={hcInputStyle(false)} value={f.repeat} onChange={e => setField('repeat', e.target.value)}>
                                            <option value="every_day">Every Day</option>
                                            <option value="selected_days">Selected Days</option>
                                        </select>
                                    </HCField>
                                )}
                            </div>

                            {f.duration === 'custom' && (
                                <HCField label="Repeat" required>
                                    <select style={hcInputStyle(false)} value={f.repeat} onChange={e => setField('repeat', e.target.value)}>
                                        <option value="every_day">Every Day</option>
                                        <option value="selected_days">Selected Days</option>
                                    </select>
                                </HCField>
                            )}

                            {f.repeat === 'selected_days' && (
                                <HCField label="Days" required error={errs.selectedDays}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(64px, 1fr))', gap: 8 }}>
                                        {WEEKDAYS.map(d => (
                                            <div key={d.key} style={hcDayChip(f.selectedDays.includes(d.key))} onClick={() => toggleDay(d.key)}>
                                                {d.label}
                                            </div>
                                        ))}
                                    </div>
                                </HCField>
                            )}

                            <HCField label="Time(s)" required error={errs.times} hint="Times already past today will be skipped for today's dose only.">
                                {f.times.map((t, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="time"
                                            style={{ ...hcInputStyle(false), flex: '1 1 auto' }}
                                            value={t}
                                            onChange={e => setTimeAt(i, e.target.value)} />
                                        <div style={hcIconBtn} onClick={() => removeTime(i)} title="Remove time">
                                            <FaTrashAlt />
                                        </div>
                                    </div>
                                ))}
                                <div style={{ ...hcSegBtn(false), flex: '0 1 160px', textAlign: 'center' }} onClick={addTime}>
                                    <FaPlus style={{ marginRight: 6 }} /> Add another time
                                </div>
                            </HCField>
                        </>
                    )}

                    <HCField label="Notes" style={{ marginBottom: 6 }}>
                        <textarea rows={3} maxLength="500" style={{ ...hcInputStyle(false), resize: 'vertical', minHeight: 70 }} value={f.notes} onChange={e => setField('notes', e.target.value)} placeholder="Special instructions…" />
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>
                        Cancel
                    </button>
                    <button onClick={submit} type="button" disabled={saving} style={hcSaveBtn(saving)}>
                        {saving ? 'Saving…' : '✓ Schedule Medication'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EditScheduleModal = ({ log, onClose, onSaved, doFetch, toast }) => {
    const dt = log.scheduledTime ? new Date(log.scheduledTime).toISOString().slice(0, 16) : '';
    const [f, setF] = useState({ scheduledTime: dt, dosage: log.dosage || '', notes: log.notes || '' });
    const [saving, setSaving] = useState(false);
    const [editErr, setEditErr] = useState('');
    const setField = (k, v) => setF(p => ({ ...p, [k]: v }));

    const submit = async () => {
        setEditErr('');
        if (!f.scheduledTime) { setEditErr('Scheduled date & time is required.'); return; }
        if (!f.dosage.trim()) { setEditErr('Dosage is required.'); return; }
        setSaving(true);
        const r = await doFetch(`/head-caregiver/schedule/${log._id}`, { method: 'PUT', body: JSON.stringify(f) });
        setSaving(false);
        if (r.success) { toast('Schedule updated.'); onSaved(r.data); onClose(); }
        else toast(r.message || 'Failed.', 'error');
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaEdit />} title="Edit Schedule" onClose={onClose} />
                <div style={hcBodyStyle}>
                    {editErr && (<div className="validation-banner"><FaExclamationTriangle /> {editErr}</div>)}
                    <div className="edit-sched-info"><strong>{log.residentName}</strong> — {log.medicationName}</div>
                    <HCField label="Scheduled Date & Time">
                        <input type="datetime-local" style={hcInputStyle(false)} value={f.scheduledTime} onChange={e => setField('scheduledTime', e.target.value)} />
                    </HCField>
                    <HCField label="Dosage">
                        <input style={hcInputStyle(false)} value={f.dosage} onChange={e => setField('dosage', e.target.value)} placeholder="e.g. 1 tablet" />
                    </HCField>
                    <HCField label="Notes">
                        <textarea rows={3} maxLength="500" style={{ ...hcInputStyle(false), minHeight: 70, resize: 'vertical' }} value={f.notes} onChange={e => setField('notes', e.target.value)} />
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>Cancel</button>
                    <button onClick={submit} type="button" disabled={saving} style={hcSaveBtn(saving)}>
                        {saving ? 'Saving…' : '✓ Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Same look as the filled-Approve / outlined-Decline buttons in the Stock
// Requests table (Dashboard.css only styles those inside a table cell's
// `.actions`, so they render as bare text anywhere else).
const flagActionBtn = (variant, busy) => ({
    flex: 1,
    padding: '7px 10px',
    borderRadius: 6,
    fontSize: '.82rem',
    fontWeight: 700,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.5 : 1,
    border: `1.5px solid ${variant === 'approve' ? '#1E7D56' : '#C0392B'}`,
    background: variant === 'approve' ? '#1E7D56' : '#fff',
    color: variant === 'approve' ? '#fff' : '#C0392B',
});

// Read-only look at one flagged medication so the Head Caregiver can
// double-check it before approving. Reuses the Admin "Register Medication"
// popup's shell (mfr-* classes) and the same MedicationFlagPhotos gallery
// used on the cards. Caregivers only submit photos + barcode, so the medicine
// details come from reading the photos on request ("Read details from
// photos") and are display-only — nothing here is editable.
const MedicationFlagDetailsModal = ({ flag, flaggerName, submittedAt, busy, reading, onDuty, onResolve, onRead, onClose }) => {
    const approveStyle = flagActionBtn('approve', busy || !onDuty);
    const declineStyle = flagActionBtn('decline', busy || !onDuty);
    const offDutyTitle = onDuty ? undefined : 'Not available while off duty';
    const photoCount = (flag.photos || []).length;

    const read = flag.extractedData || null;
    const readError = flag.extractionError || null;

    let notice;
    let noticeBtn = null;
    if (read && read.name) {
        notice = { tone: 'info', text: 'Details were read automatically from the photos and can\u2019t be edited here. Please check them against the packaging before approving. Anything the photos didn\u2019t clearly show is left blank.' };
    } else if (readError) {
        notice = { tone: 'warn', text: `The photos couldn't be auto-read (${String(readError).slice(0, 120)}). Use the pictures above to check the packaging yourself.` };
        noticeBtn = 'Try again';
    } else if (read) {
        notice = { tone: 'warn', text: 'No product name could be read from the photos. Use the pictures above to check the packaging yourself. Photos of the front of the box (name and strength) work best.' };
        noticeBtn = 'Read again';
    } else {
        notice = { tone: 'info', text: 'Caregivers submit the barcode and photos only. Tap \u201cRead details from photos\u201d to have the label read automatically, or use the pictures above to check the packaging yourself.' };
        noticeBtn = 'Read details from photos';
    }

    return (
        <div className="modal-overlay">
            <div className="mfr-modal" role="dialog" aria-modal="true" aria-label="Medication details">
                <div className="mfr-header">
                    <h5 className="mfr-title">Medication Details — Barcode {flag.barcode}</h5>
                    <button type="button" className="mfr-close" onClick={onClose} aria-label="Close">&times;</button>
                </div>

                <div className="mfr-body">
                    <MedicationFlagPhotos photos={flag.photos} heroHeight={280} />

                    <div className={`mfr-banner mfr-banner-${notice.tone}`}>
                        <span className="mfr-banner-text">{notice.text}</span>
                        {noticeBtn && photoCount > 0 && (
                            <button
                                type="button"
                                className="mfr-banner-btn"
                                disabled={reading || busy || !onDuty}
                                title={offDutyTitle}
                                onClick={() => onRead(flag)}
                            >
                                {reading ? 'Reading…' : noticeBtn}
                            </button>
                        )}
                    </div>
                    {reading && (
                        <div className="mfr-banner mfr-banner-info">
                            <span className="mfr-banner-text">Reading the photos — this can take a little while. Please keep this window open.</span>
                        </div>
                    )}

                    <MedicationFlagDetailFields read={read} />

                    <div className="mfr-grid">
                        <div className="mfr-field">
                            <div className="mfr-label">Barcode</div>
                            <div className="mfr-input">{flag.barcode}</div>
                        </div>
                        <div className="mfr-field">
                            <div className="mfr-label">Photos Submitted</div>
                            <div className="mfr-input">{photoCount}</div>
                        </div>
                        <div className="mfr-field">
                            <div className="mfr-label">Flagged By</div>
                            <div className="mfr-input">{flaggerName}</div>
                        </div>
                        {submittedAt && (
                            <div className="mfr-field">
                                <div className="mfr-label">Submitted</div>
                                <div className="mfr-input">{submittedAt}</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mfr-footer">
                    <button
                        type="button"
                        className="mfr-btn"
                        disabled={busy || !onDuty}
                        title={offDutyTitle}
                        onClick={() => onResolve(flag, 'rejected')}
                        style={{ background: declineStyle.background, color: declineStyle.color, border: declineStyle.border }}
                    >
                        Decline
                    </button>
                    <button
                        type="button"
                        className="mfr-btn"
                        disabled={busy || !onDuty}
                        title={offDutyTitle}
                        onClick={() => onResolve(flag, 'approved')}
                        style={{ background: approveStyle.background, color: approveStyle.color, border: approveStyle.border }}
                    >
                        Approve
                    </button>
                </div>
            </div>
        </div>
    );
};

// Caregiver-flagged "this barcode isn't in the system" reports, waiting on
// a Head Caregiver approve/reject decision before Admin ever sees them.
const MedicationFlagsPanel = ({ doFetch, toast, onDuty }) => {
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [selectedFlag, setSelectedFlag] = useState(null);
    const [readingId, setReadingId] = useState(null);

    const fetchFlags = useCallback(async () => {
        setLoading(true);
        const r = await doFetch('/head-caregiver/medication-flags');
        setFlags(r.success ? (r.data || []) : []);
        setLoading(false);
    }, [doFetch]);

    useEffect(() => { fetchFlags(); }, [fetchFlags]);

    const resolve = async (flag, status) => {
        if (status === 'rejected' && !window.confirm(`Reject the flagged medication (barcode ${flag.barcode})? This cannot be undone.`)) {
            return false;
        }
        setProcessingId(flag._id);
        const r = await doFetch(`/head-caregiver/medication-flags/${flag._id}`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        });
        setProcessingId(null);
        if (r.success) {
            setFlags(prev => prev.filter(f => f._id !== flag._id));
            toast(status === 'approved' ? 'Approved — sent to Admin for registration.' : 'Medication flag rejected.');
        } else {
            toast(r.message || 'Failed to update flag.', 'error');
        }
        return !!r.success;
    };

    // Used by the details modal: same resolve() (and Decline confirm) as the
    // card buttons, then close the modal once the flag has been resolved.
    const resolveFromModal = async (flag, status) => {
        const ok = await resolve(flag, status);
        if (ok) setSelectedFlag(null);
    };

    // Reads the photos server-side and stores the result on the flag so it
    // survives refreshes and is reused when the flag is approved.
    const readDetails = async (flag) => {
        setReadingId(flag._id);
        const r = await doFetch(`/head-caregiver/medication-flags/${flag._id}/read-details`, { method: 'POST' });
        setReadingId(null);
        if (!r.success) {
            toast(r.message || 'Failed to read the photos.', 'error');
            return;
        }
        const { extractedData, extractionError } = r.data || {};
        const patch = { extractedData: extractedData || null, extractionError: extractionError || null };
        setFlags(prev => prev.map(f => (f._id === flag._id ? { ...f, ...patch } : f)));
        setSelectedFlag(prev => (prev && prev._id === flag._id ? { ...prev, ...patch } : prev));
    };

    const flaggerName = (f) => {
        const u = f.flaggedBy;
        if (!u) return '—';
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || '—';
    };

    return (
        <div className="card-white mb-18">
            <div className="card-header">
                <h5>Unregistered Medication Flags {flags.length > 0 && `(${flags.length})`}</h5>
                <button className="btn-outline-sm" onClick={fetchFlags}><FaSync className={loading ? 'spin' : ''} /></button>
            </div>
            {flags.length === 0 ? (
                <div className="text-center no-data-italic" style={{ padding: '1.5rem' }}>
                    {loading ? 'Loading…' : 'No pending medication flags.'}
                </div>
            ) : (
                <MedicationFlagCardRow count={flags.length}>
                    {flags.map(f => {
                        const busy = processingId === f._id;
                        return (
                            <div
                                key={f._id}
                                onClick={() => setSelectedFlag(f)}
                                style={{ flex: '0 0 230px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', border: '1.5px solid #E8D6CC', borderRadius: 10, padding: 12, background: '#fff', cursor: 'pointer' }}
                            >
                                {/* Photo taps keep their own behavior (switch thumbnail / open full size). */}
                                <div onClick={e => e.stopPropagation()} style={{ display: 'flow-root', minHeight: 182 }}>
                                    <MedicationFlagPhotos photos={f.photos} heroHeight={130} />
                                </div>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    title="Tap to view medication details"
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFlag(f); } }}
                                >
                                    <div style={{ fontSize: '.88rem', fontWeight: 700 }}>Barcode: {f.barcode}</div>
                                    <div style={{ fontSize: '.76rem', color: '#7A5C4E', margin: '2px 0 10px' }}>
                                        Flagged by {flaggerName(f)}
                                        {f.createdAt && <><br />{new Date(f.createdAt).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}</>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }} onClick={e => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        disabled={busy || !onDuty}
                                        title={onDuty ? undefined : 'Not available while off duty'}
                                        onClick={() => resolve(f, 'approved')}
                                        style={flagActionBtn('approve', busy || !onDuty)}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        disabled={busy || !onDuty}
                                        title={onDuty ? undefined : 'Not available while off duty'}
                                        onClick={() => resolve(f, 'rejected')}
                                        style={flagActionBtn('decline', busy || !onDuty)}
                                    >
                                        Decline
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </MedicationFlagCardRow>
            )}

            {selectedFlag && (
                <MedicationFlagDetailsModal
                    flag={selectedFlag}
                    flaggerName={flaggerName(selectedFlag)}
                    submittedAt={selectedFlag.createdAt ? new Date(selectedFlag.createdAt).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : null}
                    busy={processingId === selectedFlag._id}
                    reading={readingId === selectedFlag._id}
                    onDuty={onDuty}
                    onResolve={resolveFromModal}
                    onRead={readDetails}
                    onClose={() => setSelectedFlag(null)}
                />
            )}
        </div>
    );
};

const RequestStockModal = ({ items, onClose, doFetch, toast, onSubmitted }) => {
    const [f, setF] = useState({ productId: '', quantity: '', reason: '' });
    const [errs, setErrs] = useState({});
    const [saving, setSaving] = useState(false);
    const setField = (k, v) => { setF(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

    const selected = items.find(i => i._id === f.productId) || null;

    const validate = () => {
        const e = {};
        if (!f.productId) e.productId = 'Select an item from Admin Central Inventory.';
        if (f.quantity === '' || f.quantity === null || f.quantity === undefined) e.quantity = 'Quantity is required.';
        else if (isNaN(f.quantity)) e.quantity = 'Quantity must be a valid number.';
        else if (Number(f.quantity) <= 0) e.quantity = 'Quantity must be greater than 0.';
        return e;
    };

    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrs(e); return; }
        setSaving(true);
        const r = await doFetch('/head-caregiver/inventory/request', {
            method: 'POST',
            body: JSON.stringify({ productId: f.productId, quantity: Number(f.quantity), reason: f.reason }),
        });
        setSaving(false);
        if (r.success) { toast('Stock request submitted.'); onSubmitted?.(); onClose(); }
        else toast(r.message || 'Failed.', 'error');
    };

    return (
        <div className="modal-overlay">
            <div className="registration-modal" style={hcModalStyle}>
                <HCHeader icon={<FaBoxOpen />} title="Request Stock Replenishment" onClose={onClose} />
                <div style={hcBodyStyle}>
                    <HCField label="Select Item" required error={errs.productId}>
                        <select style={hcInputStyle(errs.productId)} value={f.productId} onChange={e => setField('productId', e.target.value)}>
                            <option value="">Choose from Admin Central Inventory…</option>
                            {items.map(i => <option key={i._id} value={i._id}>{i.name} — {i.unit}</option>)}
                        </select>
                        {items.length === 0 && (
                            <p style={{ margin: '6px 2px 0', fontSize: '.78rem', color: '#7A5C4E' }}>
                                No products available in Admin Central Inventory yet.
                            </p>
                        )}
                    </HCField>
                    {selected && (
                        <p style={{ margin: '-4px 2px 10px', fontSize: '.82rem', color: '#7A5C4E' }}>
                            {selected.name} — unit: <strong>{selected.unit}</strong> · your current assigned stock: <strong>{selected.quantity} {selected.unit}</strong>
                        </p>
                    )}
                    <HCField label="Quantity Needed" required error={errs.quantity}>
                        <input type="number" min="1" step="1" style={hcInputStyle(errs.quantity)} value={f.quantity} onChange={e => setField('quantity', e.target.value)} />
                    </HCField>
                    <HCField label="Reason / Notes" style={{ marginBottom: 6 }}>
                        <textarea rows={3} style={{ ...hcInputStyle(false), resize: 'vertical', minHeight: 70 }} value={f.reason} onChange={e => setField('reason', e.target.value)} placeholder="Why is this stock needed?" />
                    </HCField>
                </div>
                <div style={hcFooter}>
                    <button onClick={onClose} type="button" disabled={saving} style={hcCancelBtn(saving)}>
                        Cancel
                    </button>
                    <button onClick={submit} type="button" disabled={saving} style={hcSaveBtn(saving)}>
                        {saving ? 'Saving…' : '✓ Submit Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const HeadCaregiverDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const doFetch = useFetch();

    const [activeSection, setSection] = useState(location.state?.section || 'home');
    const [searchQuery, setSearch] = useState('');
    const [accountMenuOpen, setAcctMenu] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [filterStatus, setFStatus] = useState('All');
    const [filterResident, setFRes] = useState('All');
    const [filterCaregiver, setFCaregiver] = useState('All');
    const [sortTime, setSort] = useState('Asc');
    const [resSort, setResSort] = useState('AZ');
    const [resFloor, setResFloor] = useState('All');
    const [resRoom, setResRoom] = useState('All');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [residents, setResidents] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [medications, setMedications] = useState([]);

    const [inventory, setInventory] = useState([]);

    const [assignedStock, setAssignedStock] = useState([]);

    const [pendingScheduleIds, setPendingScheduleIds] = useState(() => new Set());

    const [products, setProducts] = useState([]);

    const [stockRequests, setStockRequests] = useState([]);

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifOpen, setNotifOpen] = useState(false);
    const notifRef = useRef(null);
    const [caregivers, setCaregivers] = useState([]);
    const [stats, setStats] = useState({
        total: 0, onTime: 0, delayed: 0, missed: 0,
        pending: 0, overdue: 0, complianceRate: 0,
        lowMedStock: 0, totalResidents: 0
    });

    const [modal, setModal] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [resPage, setResPage] = useState(1);
    const [schedPage, setSchedPage] = useState(1);
    const [activeMedsPage, setActiveMedsPage] = useState(1);
    const [invPage, setInvPage] = useState(1);
    const [stockPage, setStockPage] = useState(1);
    const [requestsPage, setRequestsPage] = useState(1);
    const [stockCategoryFilter, setStockCategoryFilter] = useState('All');
    const [stockStatusFilter, setStockStatusFilter] = useState('All');
    const PER = 5;

    const shiftLabel = {
        DAY: 'Day (7AM–7PM)',
        NIGHT: 'Night (7PM–7AM)',
        FLEXIBLE: 'Flexible',
    }[user?.shift] || 'Day (7AM–7PM)';

    const onDuty = (() => {
        if (user?.shift !== 'DAY' && user?.shift !== 'NIGHT') return true;
        const manilaNow = new Date(Date.now() + 8 * 60 * 60 * 1000);
        const minutes = manilaNow.getUTCHours() * 60 + manilaNow.getUTCMinutes();
        const isDayWindow = minutes >= 7 * 60 && minutes < 19 * 60;
        return user.shift === 'DAY' ? isDayWindow : !isDayWindow;
    })();

    const ON_DUTY_ONLY_MODALS = new Set([
        'addResident', 'editResident', 'discharge', 'assignCaregiver',
        'addSchedule', 'editSchedule', 'deleteMedication', 'requestStock',
    ]);
    const openModal = (next) => {
        if (next?.type && ON_DUTY_ONLY_MODALS.has(next.type) && !onDuty) {
            toast('Not available while off duty.', 'error');
            return;
        }
        setModal(next);
    };

    const [showOffDutyNotice, setShowOffDutyNotice] = useState(() => !onDuty);

    const toast = useCallback((msg, type = 'success') => {
        const id = Date.now();
        setToasts(t => [...t, { id, msg, type }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
    }, []);

    const fetchCaregivers = useCallback(async () => {
        const r = await doFetch('/head-caregiver/caregivers');
        if (r.success) {
            const caregiverList = r.data || [];
            if (caregiverList.length > 0) {
                setCaregivers(caregiverList);
                return;
            }
        }

        const staffR = await doFetch('/admin/staff');
        if (staffR.success) {
            const staff = staffR.staff || staffR.data || [];
            setCaregivers(staff
                .filter(member =>
                    String(member.role || '').toLowerCase() === 'caregiver' &&
                    !['terminated', 'deactivated'].includes(String(member.status || '').toLowerCase())
                )
                .map(member => ({
                    _id: member._id,
                    name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                    firstName: member.firstName || '',
                    lastName: member.lastName || '',
                    role: member.role,
                    email: member.email,
                    staffId: member.staffId,
                    status: member.status
                })));
        }
    }, [doFetch]);

    const loadAll = useCallback(async () => {
        const [resR, schR, medR, invR, stR, assignedR, prodR, reqR, alertR, unreadR] = await Promise.all([
            doFetch('/head-caregiver/residents'),
            doFetch('/head-caregiver/schedule'),
            doFetch('/head-caregiver/medications'),

            doFetch('/head-caregiver/inventory'),
            doFetch('/head-caregiver/stats'),
            doFetch('/head-caregiver/assigned-stock'),

            doFetch('/head-caregiver/products'),

            doFetch('/head-caregiver/stock-requests'),

            doFetch('/alerts'),
            doFetch('/alerts/unread-count'),
        ]);
        if (resR.success) setResidents(resR.data || []);
        if (schR.success) setSchedule(schR.data || []);
        if (medR.success) setMedications(medR.data || []);
        if (invR.success) setInventory(invR.data || []);
        if (stR.success) setStats(s => ({ ...s, ...stR.data }));
        if (assignedR.success) setAssignedStock(assignedR.data || []);
        if (prodR.success) setProducts(prodR.data || []);
        if (reqR.success) setStockRequests(reqR.data || []);
        if (alertR.success) setNotifications(alertR.data || []);
        if (unreadR.success) setUnreadCount(unreadR.count || 0);
        await fetchCaregivers();
    }, [doFetch, fetchCaregivers]);

    const refreshNotifications = useCallback(async () => {
        const [alertR, unreadR] = await Promise.all([
            doFetch('/alerts'),
            doFetch('/alerts/unread-count'),
        ]);
        if (alertR.success) setNotifications(alertR.data || []);
        if (unreadR.success) setUnreadCount(unreadR.count || 0);
    }, [doFetch]);

    const markNotificationRead = useCallback(async (id) => {
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        setUnreadCount(c => Math.max(0, c - 1));
        const r = await doFetch(`/alerts/${id}/read`, { method: 'PUT' });
        if (!r.success) {

            await refreshNotifications();
        }
    }, [doFetch, refreshNotifications]);

    const markAllNotificationsRead = useCallback(async () => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
        const r = await doFetch('/alerts/mark-all-read', { method: 'PUT' });
        if (!r.success) {
            await refreshNotifications();
        }
    }, [doFetch, refreshNotifications]);

    const refreshStockRequests = useCallback(async () => {
        const r = await doFetch('/head-caregiver/stock-requests');
        if (r.success) setStockRequests(r.data || []);
    }, [doFetch]);

    const refreshStats = useCallback(async () => {
        const r = await doFetch('/head-caregiver/stats');
        if (r.success) setStats(s => ({ ...s, ...r.data }));
    }, [doFetch]);

    const refreshResidentsPage = useCallback(async () => {
        setRefreshing(true);
        const [resR] = await Promise.all([doFetch('/head-caregiver/residents'), fetchCaregivers()]);
        if (resR.success) setResidents(resR.data || []);
        setRefreshing(false);
    }, [doFetch, fetchCaregivers]);

    const refreshMedicinesPage = useCallback(async () => {
        setRefreshing(true);
        const [schR, medR] = await Promise.all([
            doFetch('/head-caregiver/schedule'),
            doFetch('/head-caregiver/medications'),
        ]);
        if (schR.success) setSchedule(schR.data || []);
        if (medR.success) setMedications(medR.data || []);
        await refreshStats();
        setRefreshing(false);
    }, [doFetch, refreshStats]);

    const refreshStockPage = useCallback(async () => {
        setRefreshing(true);
        const [invR, assignedR, prodR, reqR] = await Promise.all([
            doFetch('/head-caregiver/inventory'),
            doFetch('/head-caregiver/assigned-stock'),
            doFetch('/head-caregiver/products'),
            doFetch('/head-caregiver/stock-requests'),
        ]);
        if (invR.success) setInventory(invR.data || []);
        if (assignedR.success) setAssignedStock(assignedR.data || []);
        if (prodR.success) setProducts(prodR.data || []);
        if (reqR.success) setStockRequests(reqR.data || []);
        setRefreshing(false);
    }, [doFetch]);

    useEffect(() => {
        (async () => { setLoading(true); await loadAll(); setLoading(false); })();
    }, [loadAll]);

    const { on, off } = useSocket();
    useEffect(() => {
        const handleResidentsUpdated = () => { loadAll(); };
        on('residentsUpdated', handleResidentsUpdated);
        return () => off('residentsUpdated', handleResidentsUpdated);
    }, [on, off, loadAll]);

    useEffect(() => {
        setResPage(1);
        setSchedPage(1);
        setActiveMedsPage(1);
        setInvPage(1);
        setStockPage(1);
        setRequestsPage(1);
    }, [searchQuery, filterStatus, filterResident, filterCaregiver, activeSection, resFloor, resRoom, resSort, stockCategoryFilter, stockStatusFilter]);

    useEffect(() => {
        if (location.state?.section) {
            setSection(location.state.section);

            navigate(location.pathname, { replace: true, state: {} });
        }

    }, [location.state?.section]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadAll();
        setRefreshing(false);
    };

    const RefreshBtn = ({ onClick = handleRefresh, title = 'Refresh data' }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={refreshing}
            title={title}
            aria-label={title}
            className="refresh-icon-btn"
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                border: '1.5px solid #D9762B', background: '#fff', color: '#D9762B',
                cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.7 : 1,
            }}
        >
            <FaSync className={refreshing ? 'spin' : ''} style={{ fontSize: '.8rem' }} />
        </button>
    );

    useEffect(() => {
        if (!notifOpen) return;
        const handleClickOutside = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [notifOpen]);

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const handleLogout = () => setShowLogoutConfirm(true);
    const confirmLogout = async () => {
        await logout();
        navigate(HIDDEN_LOGIN_PATH);
    };

    const markStatus = async (id, status, method = 'manual') => {

        if (pendingScheduleIds.has(id)) return;

        const log = schedule.find(l => l._id === id);
        const resName = log?.residentName || 'this resident';
        const medName = log?.medicationName || 'this medication';

        const actionLabels = {
            completed: { msg: `Confirm: Mark ${medName} as administered for ${resName}?` },
            administered: { msg: `Confirm: Mark ${medName} as administered for ${resName}?` },
            missed: { msg: `Mark ${medName} as MISSED for ${resName}? This cannot be undone.` },
            skipped: { msg: `Mark ${medName} as skipped for ${resName}?` },
        };

        if (actionLabels[status]) {
            const confirmed = window.confirm(actionLabels[status].msg);
            if (!confirmed) return;
        }

        setPendingScheduleIds(prev => new Set(prev).add(id));
        try {
            const r = await doFetch(`/head-caregiver/schedule/${id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status, verificationMethod: method })
            });

            if (r.success) {
                setSchedule(prev => prev.map(l => l._id === id ? {
                    ...l,
                    status,
                    ...(status === 'completed' || status === 'administered' ? { administeredTime: new Date().toISOString() } : {})
                } : l));

                const isAdministerAction = status === 'completed' || status === 'administered';
                if (isAdministerAction) {

                    const [invR, assignedR] = await Promise.all([
                        doFetch('/head-caregiver/inventory'),
                        doFetch('/head-caregiver/assigned-stock'),
                    ]);
                    if (invR.success) setInventory(invR.data || []);
                    if (assignedR.success) setAssignedStock(assignedR.data || []);
                }

                await refreshStats();
                toast(`${medName} marked as ${status} for ${resName}.`);
            } else {
                toast(r.message || 'Update failed.', 'error');
            }
        } finally {
            setPendingScheduleIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const filteredSched = useMemo(() => {
        const q = searchQuery.toLowerCase();
        let arr = schedule.filter(l => {
            const mQ = !q || l.residentName?.toLowerCase().includes(q) || l.medicationName?.toLowerCase().includes(q) || l.room?.toLowerCase().includes(q);
            const mSt = filterStatus === 'All' || l.status === filterStatus.toLowerCase();
            const mR = filterResident === 'All' || l.residentName === filterResident;
            const mC = filterCaregiver === 'All' || String(l.caregiverId || '') === filterCaregiver;
            return mQ && mSt && mR && mC;
        });

        const getTime = (l) => new Date(l.scheduledTime || l.createdAt).getTime();
        const dir = sortTime === 'Asc' ? 1 : -1;

        // Group so every dose of the same medication for the same resident
        // stays together (e.g. recurring schedules), instead of interleaving
        // with other residents'/medications' rows that happen to fall at the
        // same time. Groups themselves are still ordered by time, and doses
        // within a group are sorted by time too, so the chosen Sort: Time
        // direction is preserved overall.
        const groups = new Map();
        for (const item of arr) {
            const key = `${item.residentId || item.residentName || ''}::${item.medicationId || item.medicationName || ''}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        }

        const groupArrs = [...groups.values()].map(g => [...g].sort((a, b) => dir * (getTime(a) - getTime(b))));
        groupArrs.sort((ga, gb) => dir * (getTime(ga[0]) - getTime(gb[0])));

        return groupArrs.flat();
    }, [schedule, searchQuery, filterStatus, filterResident, filterCaregiver, sortTime]);

    const filteredRes = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        let arr = residents.filter(r => {
            if (!q) return true;
            const name = (r.name || `${r.firstName || ''} ${r.lastName || ''}`).toLowerCase();
            const nickname = (r.nickname || '').toLowerCase();
            const room = (r.room || '').toLowerCase();
            const condMatch = (r.conditions || []).some(c =>
                (typeof c === 'string' ? c : c?.name || '').toLowerCase().includes(q)
            );
            return name.includes(q) || nickname.includes(q) || room.includes(q) || condMatch;
        });
        if (filterStatus !== 'All') {
            arr = arr.filter(r => (r.alertLevel || 'stable').toLowerCase() === filterStatus.toLowerCase());
        }
        if (filterCaregiver !== 'All') {
            arr = arr.filter(r => String(r.primaryCaregiverId?._id || r.primaryCaregiverId || '') === filterCaregiver);
        }
        if (resFloor !== 'All') {
            arr = arr.filter(r => r.floor === resFloor);
            if (resRoom !== 'All') {
                arr = arr.filter(r => (r.room || '') === resRoom);
            }
        }
        arr = [...arr].sort((a, b) => {
            const an = (a.name || `${a.firstName || ''} ${a.lastName || ''}`).trim().toLowerCase();
            const bn = (b.name || `${b.firstName || ''} ${b.lastName || ''}`).trim().toLowerCase();
            return resSort === 'AZ' ? an.localeCompare(bn) : bn.localeCompare(an);
        });
        return arr;
    }, [residents, searchQuery, filterStatus, filterCaregiver, resFloor, resRoom, resSort]);

    const residentNames = useMemo(() => ['All', ...new Set(schedule.map(l => l.residentName).filter(Boolean))], [schedule]);

    const groupedByResident = useMemo(() => {
        const g = {};
        schedule.forEach(l => {
            const key = l.residentName || 'Unknown';
            if (!g[key]) g[key] = { name: key, room: l.room, floor: l.floor, residentId: l.residentId, meds: [] };
            g[key].meds.push(l);
        });
        return Object.values(g);
    }, [schedule]);

    const filteredGroupedByResident = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return groupedByResident;
        return groupedByResident
            .map(grp => ({
                ...grp,
                meds: grp.meds.filter(m =>
                    grp.name?.toLowerCase().includes(q) ||
                    m.medicationName?.toLowerCase().includes(q) ||
                    grp.room?.toLowerCase().includes(q)
                )
            }))
            .filter(grp => grp.meds.length > 0);
    }, [groupedByResident, searchQuery]);

    const filteredInventory = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return inventory;
        return inventory.filter(item => item.name?.toLowerCase().includes(q));
    }, [inventory, searchQuery]);

    const filteredAssignedStock = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return assignedStock.filter(item => {
            if (q && !item.name?.toLowerCase().includes(q)) return false;
            if (stockCategoryFilter !== 'All' && getStockCategoryGroup(item.category) !== stockCategoryFilter) return false;
            if (stockStatusFilter !== 'All' && item.status !== stockStatusFilter) return false;
            return true;
        });
    }, [assignedStock, searchQuery, stockCategoryFilter, stockStatusFilter]);

    const requestableItems = useMemo(() => {
        return products.map(p => {
            const assigned = assignedStock.find(a => String(a.productId) === String(p._id));
            return {
                _id: p._id,
                name: p.name,
                unit: p.unit,
                quantity: assigned ? assigned.quantity : 0,
            };
        });
    }, [products, assignedStock]);

    const getMinutesSince = iso => iso ? Math.round((Date.now() - new Date(iso).getTime()) / 60000) : null;

    const SchedActionBtn = ({ item }) => {
        const isPending = pendingScheduleIds.has(item._id);
        if (item.status === 'overdue')
            return <button className="sched-btn-verify" disabled={isPending || !onDuty} title={onDuty ? undefined : "Not available while off duty"} onClick={() => markStatus(item._id, 'completed', 'manual')}>{isPending ? 'Verifying…' : 'Verify Now'}</button>;
        if (item.status === 'scheduled' || item.status === 'upcoming')
            return <button className="sched-btn-prepare" disabled={isPending} onClick={() => markStatus(item._id, 'pending', 'manual')}>{isPending ? 'Preparing…' : 'Prepare'}</button>;
        if (item.status === 'pending')
            return <button className="sched-btn-view" disabled style={{ opacity: 0.7 }}>Prepared — Awaiting Caregiver</button>;
        if (item.status === 'completed' || item.status === 'administered')
            return <button className="sched-btn-view" onClick={() => setModal({ type: 'history', data: residents.find(r => r.name === item.residentName) || { _id: item.residentId, name: item.residentName } })}>View</button>;
        return <button className="btn-success-sm sched-btn-administer" disabled={isPending || !onDuty} title={onDuty ? undefined : "Not available while off duty"} onClick={() => markStatus(item._id, 'completed')}>{isPending ? 'Administering…' : 'Administer'}</button>;
    };

    const renderHome = () => (
        <div>
            <div className="nurse-header">
                <h2>Home Dashboard</h2>
                <p className="last-login">
                    Welcome back, <strong>{user?.firstName} {user?.lastName}</strong> &nbsp;|&nbsp;
                    {new Date().toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <div className="badge-row">
                    <span className="nurse-info-pill">Shift: {shiftLabel}</span>
                    {user?.ward && <span className="nurse-info-pill">{user.ward}</span>}
                    <span className={`nurse-info-pill ${onDuty ? 'on-duty' : 'off-duty'}`}>● {onDuty ? 'On Duty' : 'Off Duty'}</span>
                    <button className="btn-outline-sm ml-auto" onClick={handleRefresh} disabled={refreshing}>
                        <FaSync className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh Data'}
                    </button>
                </div>
            </div>

            <div className="nurse-stat-row">
                {[
                    { label: 'Total Meds', val: stats.total, cls: '' },
                    { label: 'On Time', val: stats.onTime, cls: 'success' },
                    { label: 'Delayed', val: stats.delayed, cls: 'warn' },
                    { label: 'Missed', val: stats.missed, cls: 'danger' },
                    { label: 'Pending', val: stats.pending, cls: 'muted' },
                    { label: 'Residents', val: stats.totalResidents || residents.length, cls: '' },
                ].map(s => (
                    <div key={s.label} className={`nurse-stat-box ${s.cls}`}>
                        <strong>{s.val}</strong>
                        <span>{s.label}</span>
                    </div>
                ))}
            </div>

            <div className="card-white mb-18">
                <div className="compliance-text">
                    <span>Compliance Rate &nbsp;</span>
                    <strong className={`compliance-rate-value ${stats.complianceRate >= 90 ? 'excellent' : stats.complianceRate >= 70 ? 'good' : 'poor'}`}>
                        {stats.complianceRate}%
                        <span className="compliance-sub"> — {stats.complianceRate === 0 ? 'No data yet' : stats.complianceRate >= 90 ? 'Excellent' : stats.complianceRate >= 70 ? 'Good' : 'Needs Improvement'}</span>
                    </strong>
                </div>
                <div className="compliance-bar">
                    <div className="compliance-progress" style={{ width: `${stats.complianceRate}%` }} />
                </div>
            </div>

            <div className="home-top-grid">
                <div className="card-white home-card">
                    <h6><FaClock style={{ marginRight: 7, color: 'var(--d-orange)' }} />Today's Schedule</h6>
                    {schedule.length === 0 ? (
                        <div className="no-data-center"><FaPills /> No medications scheduled today.</div>
                    ) : (
                        <div className="sched-list">
                            {schedule.slice(0, 3).map(item => {
                                const mins = item.status === 'overdue' ? getMinutesSince(item.scheduledTime) : null;
                                const timeStr = item.scheduledTime ? new Date(item.scheduledTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : '—';
                                const done = item.status === 'completed' || item.status === 'administered';
                                return (
                                    <div key={item._id} className="sched-item">
                                        <span className={`sched-time ${done ? 'done' : item.status === 'overdue' ? 'overdue' : 'pending'}`}>{timeStr}</span>
                                        <div className="sched-body">
                                            <div className="sched-name">
                                                {done && <span className="done-check">✓</span>}
                                                {item.status === 'overdue' && <FaExclamationTriangle className="overdue-icon" />}
                                                {item.residentName || '—'}
                                            </div>
                                            <div className="sched-med">{item.medicationName} {item.dosage && `— ${item.dosage}`}</div>
                                            {item.status === 'overdue' && mins !== null && <div className="sched-overdue-tag">OVERDUE: {mins} min ago</div>}
                                        </div>
                                        <Badge s={item.status} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <button className="btn-primary-sm home-full-width-btn" onClick={() => setSection('medicines')}>View Full Schedule →</button>
                </div>

                <div className="card-white home-card">
                    <h6><FaUsers style={{ marginRight: 7, color: 'var(--d-orange)' }} />My Assigned Residents</h6>
                    {residents.length === 0 ? (
                        <div className="no-data-center">No assigned residents yet.</div>
                    ) : (
                        <div>
                            {residents.slice(0, 3).map((r, i) => (
                                <div key={i} className="resident-list-item">
                                    <div className="resident-list-name">
                                        {r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Unknown'}
                                        {r.nickname && <span className="nickname-tag">"{r.nickname}"</span>}
                                        {r.room && <span className="condition-tag" style={{ marginLeft: 6 }}>Room {r.room}</span>}
                                    </div>
                                    <div className="resident-list-meta">Age: {r.age || '—'} {r.conditions?.length > 0 && `· ${r.conditions.slice(0, 2).map(c => c?.name || c).join(', ')}`}</div>
                                    {r.medicationOverdue ? (
                                        <div className="resident-list-overdue"><FaExclamationCircle /> Medication Overdue</div>
                                    ) : (
                                        <div className="resident-list-nextmed">Next Med: {r.nextMed || '—'}</div>
                                    )}
                                </div>
                            ))}
                            {residents.length > 3 && <div className="list-more-label">+{residents.length - 3} more residents</div>}
                        </div>
                    )}
                    <button className="btn-primary-sm home-full-width-btn" onClick={() => setSection('residents')}>View All Residents →</button>
                </div>

                <div className="card-white home-card">
                    <h6>Quick Actions</h6>
                    <div className="quick-actions-grid">
                        {[
                            { icon: <FaPlus />, label: 'Schedule Medication', action: () => openModal({ type: 'addSchedule' }), onDutyOnly: true },
                            { icon: <FaUsers />, label: 'Add Resident', action: () => openModal({ type: 'addResident' }), onDutyOnly: true },
                            { icon: <FaBoxOpen />, label: 'Request Stock', action: () => openModal({ type: 'requestStock' }), onDutyOnly: true },
                            { icon: <FaFileAlt />, label: 'Med Reports', action: () => setSection('medicines') },
                            { icon: <FaSync />, label: 'Refresh Data', action: handleRefresh },
                        ].map((a, i) => (
                            <button key={i} className="quick-action-btn" onClick={a.action} disabled={a.onDutyOnly && !onDuty}>{a.icon} {a.label}</button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderResidents = () => {
        const RES_PER = 10;
        const pages = Math.max(1, Math.ceil(filteredRes.length / RES_PER));
        const safeResPage = Math.min(resPage, pages);
        const paged = filteredRes.slice((safeResPage - 1) * RES_PER, safeResPage * RES_PER);

        const getResidentName = (r) => {
            if (r.name) return r.name;
            const first = r.firstName || '';
            const last = r.lastName || '';
            const nickname = r.nickname ? ` "${r.nickname}"` : '';
            return `${first} ${last}`.trim() + nickname || 'Unnamed Resident';
        };

        return (
            <div>
                <div className="res-page-header">
                    <span className="res-page-label">MY ASSIGNED RESIDENTS ({residents.length})</span>
                    <div className="res-page-controls">
                        <RefreshBtn onClick={refreshResidentsPage} title="Refresh residents" />
                        <select className="filter-select" value={filterStatus} onChange={e => setFStatus(e.target.value)}>
                            <option value="All">Filter: All</option>
                            <option value="alert">Alert</option>
                            <option value="stable">Stable</option>
                            <option value="critical">Critical</option>
                        </select>
                        <select className="filter-select" value={resSort} onChange={e => setResSort(e.target.value)}>
                            <option value="AZ">Sort: A–Z</option>
                            <option value="ZA">Sort: Z–A</option>
                        </select>
                        <select
                            className="filter-select"
                            value={resFloor}
                            onChange={e => { setResFloor(e.target.value); setResRoom('All'); }}>
                            <option value="All">Floor: All</option>
                            {FLOORS.map(fl => <option key={fl} value={fl}>{fl}</option>)}
                        </select>
                        <select
                            className="filter-select"
                            value={resRoom}
                            disabled={resFloor === 'All'}
                            title={resFloor === 'All' ? 'Select a floor first' : undefined}
                            style={resFloor === 'All' ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            onChange={e => setResRoom(e.target.value)}>
                            <option value="All">Room: All</option>
                            {ROOM_NUMBERS.map(rn => <option key={rn} value={rn}>Room {rn}</option>)}
                        </select>
                        <select className="filter-select" value={filterCaregiver} onChange={e => setFCaregiver(e.target.value)}>
                            <option value="All">Caregiver: All</option>
                            {caregivers.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>
                        <button className="btn-primary-sm" onClick={() => openModal({ type: 'addResident' })} disabled={!onDuty} title={onDuty ? undefined : "Not available while off duty"}><FaPlus /> Add Resident</button>
                    </div>
                </div>

                <div className="residents-table-scroll">
                    <div className="res-col-header">
                        <span>Room | Bed</span><span>Name / Age</span><span>Conditions</span><span>Status</span><span>Today's Medication</span><span className="res-col-actions-header">Actions</span>
                    </div>

                    {paged.length === 0 ? (
                        <div className="res-row-empty">{searchQuery ? `No residents match "${searchQuery}".` : 'No residents yet.'}</div>
                    ) : (
                        paged.map((r, i) => {
                            const isLast = i === paged.length - 1;
                            const todayMeds = schedule.filter(l =>
                                l.residentName === getResidentName(r) ||
                                l.residentId?.toString() === r._id?.toString()
                            );
                            const displayName = getResidentName(r);
                            return (
                                <div key={r._id || i} className={`res-row${r.medicationOverdue ? ' overdue-row' : ''}${isLast ? ' last-row' : ''}`}>
                                    <div className="res-row-grid">
                                        <div className="res-room">
                                            {r.room || '—'} | {r.bed || '—'}
                                            <br /><small style={{ fontSize: '.72rem', color: 'var(--d-muted)' }}>{r.floor || ''}</small>
                                        </div>
                                        <div className="res-name-block">
                                            <div className="name">{displayName}</div>
                                            <div className="age">Age: {r.age || '—'} &nbsp;·&nbsp; {r.gender || ''}</div>
                                            <div className="primary">Caregiver: <span>{r.primaryCaregiverName || r.primaryCaregiver || 'Unassigned'}</span></div>
                                        </div>
                                        <div className="conditions-wrap">
                                            {r.conditions?.length > 0
                                                ? r.conditions.map((c, ci) => <span key={ci} className="condition-tag">{c?.name || c}</span>)
                                                : <span className="no-conditions">—</span>}
                                        </div>
                                        <div><Badge s={r.medicationOverdue ? 'overdue' : r.alertLevel || 'stable'} /></div>
                                        <div className="res-meds-cell">
                                            {todayMeds.length === 0 ? (
                                                <span className="res-no-meds">No meds today</span>
                                            ) : (
                                                todayMeds.slice(0, 3).map((m, mi) => (
                                                    <div key={mi} className={`res-med-item ${m.status === 'completed' || m.status === 'administered' ? 'done' : 'active'}`}>
                                                        {m.scheduledTime ? new Date(m.scheduledTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : '—'} — {m.medicationName}
                                                        {(m.status === 'completed' || m.status === 'administered') && <span className="res-med-done">✓</span>}
                                                        {m.status === 'pending' && <span className="res-med-pend">Pending</span>}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <div className="res-action-icons">
                                            <div className="res-action-icons-group">
                                                <button
                                                    className="res-action-icon"
                                                    onClick={() => setModal({ type: 'profile', data: r })}
                                                    title="View Profile"
                                                >
                                                    <FaEye />
                                                </button>
                                                <button
                                                    className="res-action-icon"
                                                    onClick={() => setModal({ type: 'history', data: r })}
                                                    title="Medication History"
                                                >
                                                    <FaPills />
                                                </button>
                                                <button
                                                    className="res-action-icon"
                                                    onClick={() => openModal({ type: 'assignCaregiver', data: r })}
                                                    title={onDuty ? "Assign Caregiver" : "Not available while off duty"}
                                                    disabled={!onDuty}
                                                >
                                                    <FaUserMd />
                                                </button>
                                                <button
                                                    className="res-action-icon"
                                                    onClick={() => openModal({ type: 'editResident', data: r })}
                                                    title={onDuty ? "Edit Resident" : "Not available while off duty"}
                                                    disabled={!onDuty}
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className="res-action-icon res-action-icon-danger"
                                                    onClick={() => openModal({ type: 'discharge', data: r })}
                                                    title={onDuty ? "Remove Resident" : "Not available while off duty"}
                                                    style={{ color: '#C0392B' }}
                                                    disabled={!onDuty}
                                                >
                                                    <FaUserMinus />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    {r.medicationOverdue && (
                                        <div className="res-overdue-alert">
                                            <FaExclamationCircle /> Medication Overdue ({getMinutesSince(r.overdueAt) || '—'} mins) — {r.overdueMed || ''}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {pages > 1 && (
                    <div className="res-page-footer">
                        <span className="res-page-label">Showing {(safeResPage - 1) * RES_PER + 1}–{Math.min(safeResPage * RES_PER, filteredRes.length)} of {filteredRes.length}</span>
                        <Pagination page={safeResPage} pages={pages} onChange={setResPage} />
                    </div>
                )}
            </div>
        );
    };

    const renderMedicines = () => {
        const schedPages = Math.max(1, Math.ceil(filteredSched.length / PER));
        const safeSchedPage = Math.min(schedPage, schedPages);
        const pagedSched = filteredSched.slice((safeSchedPage - 1) * PER, safeSchedPage * PER);

        const activeMedsPages = Math.max(1, Math.ceil(filteredGroupedByResident.length / PER));
        const safeActiveMedsPage = Math.min(activeMedsPage, activeMedsPages);
        const pagedActiveMeds = filteredGroupedByResident.slice((safeActiveMedsPage - 1) * PER, safeActiveMedsPage * PER);

        return (
            <div>
                <div className="med-pills-row">
                    <span className="nurse-info-pill">{user?.firstName} {user?.lastName}</span>
                    {user?.ward && <span className="nurse-info-pill">{user.ward}</span>}
                    <span className="nurse-info-pill">Shift: {shiftLabel.split(' ')[0]}</span>
                </div>

                <div className="med-filters-row">
                    <span className="filters-label"><FaFilter /> Filters:</span>
                    <select className="filter-select" value={filterStatus} onChange={e => setFStatus(e.target.value)}>
                        <option value="All">Status: All</option>
                        <option value="overdue">Overdue</option>
                        <option value="scheduled">Upcoming</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="missed">Missed</option>
                    </select>
                    <select className="filter-select" value={filterResident} onChange={e => setFRes(e.target.value)}>
                        {residentNames.map(r => <option key={r} value={r}>{r === 'All' ? 'Residents: All' : r}</option>)}
                    </select>
                    <select className="filter-select" value={filterCaregiver} onChange={e => setFCaregiver(e.target.value)}>
                        <option value="All">Caregiver: All</option>
                        {caregivers.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                    <select className="filter-select" value={sortTime} onChange={e => setSort(e.target.value)}>
                        <option value="Asc">Sort: Time ↑</option>
                        <option value="Desc">Sort: Time ↓</option>
                    </select>
                    <div className="med-action-btns">
                        <RefreshBtn onClick={refreshMedicinesPage} title="Refresh medication tables" />
                        <button className="btn-primary-sm" onClick={() => openModal({ type: 'addSchedule' })} disabled={!onDuty} title={onDuty ? undefined : "Not available while off duty"}><FaPlus /> Schedule Medication</button>
                    </div>
                </div>

                <div className="card-white mb-18">
                    <div className="card-header"><h5><FaClock className="mr-8" />Today's Medication Schedule</h5></div>
                    <div className="table-scroll">
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th>Status</th><th>Time</th><th>Resident</th>
                                    <th>Room</th><th>Medication</th><th>Dosage</th><th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedSched.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center no-data-italic">
                                        {searchQuery ? `No results for "${searchQuery}".` : 'No medication records found.'}
                                    </td></tr>
                                ) : (
                                    pagedSched.map(item => {
                                        const schedDate = item.scheduledTime ? new Date(item.scheduledTime) : null;
                                        const isToday = schedDate && schedDate.toDateString() === new Date().toDateString();
                                        const tStr = schedDate ? schedDate.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : '—';
                                        // Recurring schedules span multiple dates, and this list also carries
                                        // over unresolved doses from prior days — show the date whenever a
                                        // row isn't from today so grouped/overdue rows aren't mistaken for
                                        // duplicates of today's dose.
                                        const dStr = schedDate && !isToday ? schedDate.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }) : null;
                                        const mins = item.status === 'overdue' ? getMinutesSince(item.scheduledTime) : null;
                                        return (
                                            <tr key={item._id}>
                                                <td><Badge s={item.status} /></td>
                                                <td className="sched-time-cell">
                                                    {dStr && <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#b85c2d', marginBottom: 2 }}>{dStr}</div>}
                                                    {tStr}
                                                    {mins !== null && <div className="sched-overdue-min">({mins} min ago)</div>}
                                                </td>
                                                <td><strong>{item.residentName || '—'}</strong></td>
                                                <td>{item.floor && `${item.floor},`} {item.room || '—'}</td>
                                                <td>
                                                    <strong className="td-sm">{item.medicationName || '—'}</strong>
                                                    {item.condition && <div className="sched-med-condition">For: {item.condition}</div>}
                                                </td>
                                                <td className="td-sm">{item.dosage || '—'}</td>
                                                <td><SchedActionBtn item={item} /></td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    {schedPages > 1 && (
                        <div className="res-page-footer">
                            <span className="res-page-label">Showing {(safeSchedPage - 1) * PER + 1}–{Math.min(safeSchedPage * PER, filteredSched.length)} of {filteredSched.length}</span>
                            <Pagination page={safeSchedPage} pages={schedPages} onChange={setSchedPage} />
                        </div>
                    )}
                </div>

                <div className="card-white mb-18">
                    <div className="card-header"><h5>Active Medications by Resident</h5></div>
                    <div className="table-scroll">
                        <table className="custom-table">
                            <thead>
                                <tr><th>Resident</th><th>Medication</th><th>Dosage</th><th>Time</th><th>Next Dose</th><th>Status</th><th>Action</th></tr>
                            </thead>
                            <tbody>
                                {filteredGroupedByResident.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center no-data-italic">
                                        {searchQuery ? `No results for "${searchQuery}".` : 'No active medication records yet.'}
                                    </td></tr>
                                ) : (
                                    pagedActiveMeds.map(grp =>
                                        grp.meds.map((m, mi) => (
                                            <tr key={m._id}>
                                                {mi === 0 && (
                                                    <td rowSpan={grp.meds.length} className="active-med-resident-cell">
                                                        <div className="active-med-res-name">{grp.name}</div>
                                                        {grp.room && <div className="active-med-res-room">Room {grp.room}</div>}
                                                        {grp.meds.some(x => x.status === 'overdue') && <div className="active-med-overdue-tag">OVERDUE</div>}
                                                    </td>
                                                )}
                                                <td className="td-sm">{m.medicationName || '—'}</td>
                                                <td className="td-sm">{m.dosage || '—'}</td>
                                                <td className="td-xs td-nowrap">
                                                    {m.scheduledTime ? new Date(m.scheduledTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                                    {m.frequency && <div className="med-frequency">{m.frequency}</div>}
                                                </td>
                                                <td className="td-xs">{m.nextDose || '—'}</td>
                                                <td><DotBadge s={m.status} /></td>
                                                <td className="med-action-cell">
                                                    <div className="res-action-icons">
                                                        <div className="res-action-icons-group">
                                                            <button
                                                                className="res-action-icon"
                                                                onClick={() => setModal({ type: 'history', data: residents.find(r => r.name === grp.name) || { _id: m.residentId, name: grp.name } })}
                                                                title="View History"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                className="res-action-icon"
                                                                onClick={() => openModal({ type: 'addSchedule', data: { residentId: m.residentId } })}
                                                                title={onDuty ? "Schedule Medication" : "Not available while off duty"}
                                                                disabled={!onDuty}
                                                            >
                                                                <FaPlus />
                                                            </button>
                                                            <button
                                                                className="res-action-icon"
                                                                onClick={() => openModal({ type: 'editSchedule', data: m })}
                                                                title={onDuty ? "Edit Schedule" : "Not available while off duty"}
                                                                disabled={!onDuty}
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                className="res-action-icon res-action-icon-danger"
                                                                onClick={() => openModal({ type: 'deleteMedication', data: m })}
                                                                title={onDuty ? "Delete Medication" : "Not available while off duty"}
                                                                style={{ color: '#C0392B' }}
                                                                disabled={!onDuty}
                                                            >
                                                                <FaTrashAlt />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                    {activeMedsPages > 1 && (
                        <div className="res-page-footer">
                            <span className="res-page-label">Showing {(safeActiveMedsPage - 1) * PER + 1}–{Math.min(safeActiveMedsPage * PER, filteredGroupedByResident.length)} of {filteredGroupedByResident.length} residents</span>
                            <Pagination page={safeActiveMedsPage} pages={activeMedsPages} onChange={setActiveMedsPage} />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderMyStock = () => {
        const stockPages = Math.max(1, Math.ceil(filteredAssignedStock.length / PER));
        const safeStockPage = Math.min(stockPage, stockPages);
        const pagedStock = filteredAssignedStock.slice((safeStockPage - 1) * PER, safeStockPage * PER);

        const requestsPages = Math.max(1, Math.ceil(stockRequests.length / PER));
        const safeRequestsPage = Math.min(requestsPage, requestsPages);
        const pagedRequests = stockRequests.slice((safeRequestsPage - 1) * PER, safeRequestsPage * PER);

        return (
            <div>
                <div className="card-white mb-18">
                    <div className="card-header">
                        <h5>Shared Stock</h5>
                        <RefreshBtn onClick={refreshStockPage} title="Refresh stock tables" />
                    </div>
                    <p style={{ margin: '0 0 14px', padding: '0 2px', color: '#7A5C4E', fontSize: '.85rem' }}>
                        One shared pool, visible to and drawn from by every Head Caregiver
                        account — separate from Admin Central Stock. This is view-only —
                        quantities change automatically when any HC administers a dose, or
                        once a submitted stock request is approved.
                    </p>
                    <div className="med-filters-row">
                        <span className="filters-label"><FaFilter /> Filters:</span>
                        <select className="filter-select" value={stockCategoryFilter} onChange={e => setStockCategoryFilter(e.target.value)}>
                            <option value="All">Category: All</option>
                            <option value="Medicine">Medicine</option>
                            <option value="Food">Food</option>
                            <option value="Supplies">Supplies</option>
                        </select>
                        <select className="filter-select" value={stockStatusFilter} onChange={e => setStockStatusFilter(e.target.value)}>
                            <option value="All">Status: All</option>
                            <option value="In Stock">In Stock</option>
                            <option value="Low Stock">Low Stock</option>
                            <option value="Out of Stock">Out of Stock</option>
                        </select>
                    </div>
                    <div className="table-scroll">
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Category</th>
                                    <th>Quantity</th>
                                    <th>Unit</th>
                                    <th>Minimum Stock Level</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAssignedStock.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center no-data-italic">
                                        {searchQuery || stockCategoryFilter !== 'All' || stockStatusFilter !== 'All'
                                            ? 'No items match your search/filters.'
                                            : 'The shared stock pool is empty right now.'}
                                    </td></tr>
                                ) : (
                                    pagedStock.map(item => {
                                        const s = STOCK_STATUS_STYLE[item.status] || STOCK_STATUS_STYLE['In Stock'];
                                        return (
                                            <tr key={item._id}>
                                                <td><strong>{item.name}</strong></td>
                                                <td><span className="badge-custom staff">{item.category}</span></td>
                                                <td>{item.quantity}</td>
                                                <td>{item.unit}</td>
                                                <td>{item.minThreshold}</td>
                                                <td>
                                                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: '.78rem', fontWeight: 700, background: s.bg, color: s.color, border: `1.5px solid ${s.color}30` }}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    {filteredAssignedStock.length > 0 && (
                        <div className="res-page-footer">
                            <span className="res-page-label">Showing {(safeStockPage - 1) * PER + 1}–{Math.min(safeStockPage * PER, filteredAssignedStock.length)} of {filteredAssignedStock.length}</span>
                            <Pagination page={safeStockPage} pages={stockPages} onChange={setStockPage} />
                        </div>
                    )}
                </div>

                <div className="card-white mb-18">
                    <div className="card-header">
                        <h5>My Stock Requests</h5>
                        <button className="btn-primary-sm" onClick={() => openModal({ type: 'requestStock' })} disabled={!onDuty} title={onDuty ? undefined : "Not available while off duty"}>
                            <FaBoxOpen /> Request Stock
                        </button>
                    </div>
                    <div className="table-scroll">
                        <table className="custom-table">
                            <thead>
                                <tr><th>Item</th><th>Quantity</th><th>Unit</th><th>Date</th><th>Reason</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                {stockRequests.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center no-data-italic">
                                        You haven't submitted any stock requests yet.
                                    </td></tr>
                                ) : (
                                    pagedRequests.map(r => {
                                        const s = STOCK_REQUEST_STATUS_STYLE[r.status] || STOCK_REQUEST_STATUS_STYLE.pending;
                                        return (
                                            <tr key={r._id}>
                                                <td><strong>{r.itemName}</strong></td>
                                                <td>{r.quantity}</td>
                                                <td>{r.unit}</td>
                                                <td className="td-xs">{r.createdAt ? new Date(r.createdAt).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</td>
                                                <td>{r.reason || '—'}</td>
                                                <td>
                                                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: '.78rem', fontWeight: 700, background: s.bg, color: s.color, border: `1.5px solid ${s.color}30` }}>
                                                        {requestStatusLabel(r.status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    {requestsPages > 1 && (
                        <div className="res-page-footer">
                            <span className="res-page-label">Showing {(safeRequestsPage - 1) * PER + 1}–{Math.min(safeRequestsPage * PER, stockRequests.length)} of {stockRequests.length}</span>
                            <Pagination page={safeRequestsPage} pages={requestsPages} onChange={setRequestsPage} />
                        </div>
                    )}
                </div>

                <MedicationFlagsPanel doFetch={doFetch} toast={toast} onDuty={onDuty} />
            </div>
        );
    };

    const renderContent = () => {
        if (loading) return <div className="nurse-loading"><FaSpinner className="spin" /> Loading dashboard…</div>;
        switch (activeSection) {
            case 'home': return renderHome();
            case 'residents': return renderResidents();
            case 'medicines': return renderMedicines();
            case 'stock': return renderMyStock();
            default: return renderHome();
        }
    };

    return (
        <div className="dashboard-layout">
            <div className="dashboard-body">

                <div className={`sidebar nurse-sidebar${mobileMenuOpen ? ' mobile-open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="brand-section">
                            <img src={mainLogo} alt="Kanang-Alalay logo" className="logo-circle" />
                            <div className="brand-text"><h4>Kanang-Alalay</h4><h5>HEAD CAREGIVER</h5></div>
                        </div>
                    </div>
                    <ul className="sidebar-menu">
                        {[
                            { key: 'home', icon: <FaHome />, label: 'Home' },
                            { key: 'residents', icon: <FaUsers />, label: 'Residents' },
                            { key: 'medicines', icon: <FaPills />, label: 'Medication', badge: stats.overdue },
                            { key: 'stock', icon: <FaBoxOpen />, label: 'Shared Stock' },
                        ].map(({ key, icon, label, badge }) => (
                            <li key={key} className={activeSection === key ? 'active' : ''} onClick={() => { setSection(key); setMobileMenuOpen(false); }}>
                                {icon} {label}
                                {badge > 0 && <span className="sidebar-badge">{badge}</span>}
                            </li>
                        ))}
                    </ul>
                    <div className="sidebar-footer" onClick={handleLogout}><FaSignOutAlt /> <span>LOGOUT</span></div>
                </div>
                {mobileMenuOpen && <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />}

                <div className="main-content-wrapper">
                    <div className="admin-topbar nurse-topbar">
                        <div className="topbar-left">
                            <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Toggle menu">
                                <FaBars />
                            </button>
                            {activeSection !== 'home' && (
                                <div className="topbar-search-wrapper">
                                    <FaSearch className="topbar-search-icon" />
                                    <input type="text" className="topbar-search-input"
                                        placeholder={
                                            activeSection === 'residents' ? 'Search residents, rooms, nickname, conditions…' :
                                                activeSection === 'medicines' ? 'Search medications, residents…' :
                                                    activeSection === 'stock' ? 'Search the shared stock pool…' :
                                                        'Search…'
                                        }
                                        value={searchQuery}
                                        onChange={e => setSearch(e.target.value)} />
                                    {searchQuery && <button className="search-clear-btn" onClick={() => setSearch('')}><FaTimes /> Clear</button>}
                                </div>
                            )}
                        </div>
                        <div className="topbar-right">
                            <div className="topbar-notif-menu" ref={notifRef} style={{ position: 'relative' }}>
                                <button
                                    className="topbar-icon-btn"
                                    onClick={() => { const opening = !notifOpen; setNotifOpen(opening); setAcctMenu(false); if (opening) refreshNotifications(); }}
                                    title="Notifications"
                                >
                                    <FaBell />
                                    {unreadCount > 0 && (
                                        <span className="notif-dot-badge">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}
                                </button>

                                {notifOpen && (
                                    <div className="notif-dropdown" style={{ position: 'absolute', top: '100%', right: 0, width: 360, background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.15)', zIndex: 1000, marginTop: 8, overflow: 'hidden' }}>
                                        <div className="notif-dropdown-header" style={{ padding: '12px 16px', borderBottom: '1px solid #E8D6CC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 600 }}>Notifications</span>
                                            {unreadCount > 0 && (
                                                <button className="notif-action-btn" onClick={markAllNotificationsRead} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: '#F96B38' }}>Mark all as read</button>
                                            )}
                                        </div>
                                        <div className="notif-list" style={{ maxHeight: 400, overflowY: 'auto' }}>
                                            {notifications.length === 0 ? (
                                                <div className="notif-empty" style={{ textAlign: 'center', padding: '40px 20px' }}>
                                                    <FaBell style={{ fontSize: '2rem', color: '#E8D6CC' }} />
                                                    <p style={{ marginTop: 12, color: '#7A5C4E' }}>No notifications yet.</p>
                                                </div>
                                            ) : notifications.map(n => {
                                                const meta = n.type === 'stock-request-approved'
                                                    ? { color: '#28a745', icon: <FaCheck /> }
                                                    : n.type === 'stock-request-rejected'
                                                        ? { color: '#dc3545', icon: <FaTimes /> }
                                                        : { color: '#b85c2d', icon: <FaPills /> };
                                                return (
                                                    <div key={n._id}
                                                        className={`notif-item ${n.isRead ? 'read' : 'unread'}`}
                                                        onClick={() => { if (!n.isRead) markNotificationRead(n._id); }}
                                                        style={{
                                                            padding: '12px 16px',
                                                            borderBottom: '1px solid #E8D6CC',
                                                            cursor: n.isRead ? 'default' : 'pointer',
                                                            background: n.isRead ? '#fff' : '#FFF8F3',
                                                            display: 'flex',
                                                            gap: 12
                                                        }}
                                                    >
                                                        <div className="notif-item-icon" style={{ color: meta.color, fontSize: '1.1rem' }}>{meta.icon}</div>
                                                        <div className="notif-item-body" style={{ flex: 1 }}>
                                                            <strong style={{ fontSize: '0.85rem', display: 'block' }}>{n.title}</strong>
                                                            <span style={{ fontSize: '0.75rem', color: '#666' }}>{n.message}</span>
                                                            <small style={{ fontSize: '0.65rem', color: '#999', display: 'block', marginTop: 4 }}>{timeAgo(n.createdAt)}</small>
                                                        </div>
                                                        {!n.isRead && <div className="notif-unread-dot" style={{ width: 8, height: 8, background: meta.color, borderRadius: '50%', alignSelf: 'center', flexShrink: 0 }} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="topbar-user-menu">
                                <div className={`topbar-user-trigger ${accountMenuOpen ? 'active' : ''}`} onClick={() => { setAcctMenu(o => !o); setNotifOpen(false); }}>
                                    <FaUserCircle className="topbar-user-avatar" />
                                    <div className="topbar-user-info">
                                        <span className="topbar-user-name">{user?.firstName} {user?.lastName}</span>
                                        <span className="topbar-user-role">HEAD CAREGIVER</span>
                                    </div>
                                    <FaChevronDown className={`topbar-arrow ${accountMenuOpen ? 'rotate' : ''}`} />
                                </div>
                                {accountMenuOpen && (
                                    <ul className="topbar-dropdown">
                                        <li onClick={() => { navigate('/profile'); setAcctMenu(false); }}><FaUserCircle /> View Profile</li>
                                        <li onClick={() => { navigate('/settings'); setAcctMenu(false); }}><FaCog /> Account Settings</li>
                                        <li onClick={() => { navigate('/help'); setAcctMenu(false); }}><FaQuestionCircle /> Help Center</li>
                                        <li className="dropdown-divider" onClick={handleLogout}><FaSignOutAlt /> Sign Out</li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="main-content">{renderContent()}</div>
                </div>
            </div>

            {modal?.type === 'addResident' && <AddResidentModal
                onClose={() => setModal(null)}
                onSaved={r => { setResidents(p => [...p, r]); loadAll(); }}
                doFetch={doFetch}
                toast={toast}
                caregivers={caregivers}
                fetchCaregivers={fetchCaregivers}
            />}
            {modal?.type === 'editResident' && <AddResidentModal
                key={modal.data?._id}
                resident={modal.data}
                onClose={() => setModal(null)}
                onSaved={updated => { setResidents(p => p.map(r => r._id === updated._id ? { ...r, ...updated } : r)); loadAll(); }}
                doFetch={doFetch}
                toast={toast}
                caregivers={caregivers}
                fetchCaregivers={fetchCaregivers}
            />}
            {modal?.type === 'discharge' && <DischargeResidentModal
                resident={modal.data}
                onClose={() => setModal(null)}
                onSaved={removedId => { setResidents(p => p.filter(r => r._id !== removedId)); loadAll(); }}
                doFetch={doFetch}
                toast={toast}
            />}
            {modal?.type === 'profile' && <ProfileModal
                key={modal.data?._id}
                onClose={() => setModal(null)}
                resident={modal.data}
                schedule={schedule}
                onSaved={updated => setResidents(p => p.map(r => r._id === updated._id ? { ...r, ...updated } : r))}
                doFetch={doFetch}
                toast={toast}
                onDuty={onDuty}
            />}
            {modal?.type === 'assignCaregiver' && <AssignCaregiverModal
                onClose={() => setModal(null)}
                resident={modal.data}
                caregivers={caregivers}
                fetchCaregivers={fetchCaregivers}
                doFetch={doFetch}
                toast={toast}
                onSaved={updated => { setResidents(p => p.map(r => r._id === updated._id ? updated : r)); loadAll(); }}
            />}
            {modal?.type === 'history' && <HistoryModal onClose={() => setModal(null)} resident={modal.data} doFetch={doFetch} />}
            {modal?.type === 'addSchedule' && <AddScheduleModal onClose={() => setModal(null)} residents={residents} medications={medications} onSaved={l => { setSchedule(p => [...p, ...(Array.isArray(l) ? l : [l])]); refreshStats(); }} doFetch={doFetch} toast={toast} defaultResident={modal.data ? { _id: modal.data.residentId } : null} />}
            {modal?.type === 'editSchedule' && <EditScheduleModal onClose={() => setModal(null)} log={modal.data} onSaved={u => { setSchedule(p => p.map(l => l._id === u._id ? { ...l, ...u } : l)); refreshStats(); }} doFetch={doFetch} toast={toast} />}
            {modal?.type === 'deleteMedication' && <DeleteMedicationModal onClose={() => setModal(null)} log={modal.data} onSaved={id => { setSchedule(p => p.filter(l => l._id !== id)); refreshStats(); }} doFetch={doFetch} toast={toast} />}
            {modal?.type === 'requestStock' && <RequestStockModal onClose={() => setModal(null)} items={requestableItems} doFetch={doFetch} toast={toast} onSubmitted={refreshStockRequests} />}

            {showOffDutyNotice && (
                <div className="modal-overlay" style={{ zIndex: 10002 }}>
                    <div className="registration-modal" style={{ maxWidth: 380, width: '100%', padding: 'clamp(20px,6vw,28px)', boxSizing: 'border-box' }}>
                        <div className="logout-confirm-header">
                            <FaClock className="logout-confirm-icon" />
                            <h4>You're Off Duty</h4>
                        </div>
                        <p className="logout-confirm-msg">
                            You can still view residents, schedules, and inventory, but actions like adding/editing residents,
                            assigning caregivers, scheduling medications, and requesting stock are only available while on duty
                            ({shiftLabel}).
                        </p>
                        <div className="modal-footer" style={{ padding: '14px 0 0', margin: 0 }}>
                            <button className="btn-logout-confirm" onClick={() => setShowOffDutyNotice(false)}>Got It</button>
                        </div>
                    </div>
                </div>
            )}

            {showLogoutConfirm && (
                <div className="modal-overlay" style={{ zIndex: 10002 }}>
                    <div className="registration-modal" style={{ maxWidth: 380, width: '100%', padding: 'clamp(20px,6vw,28px)', boxSizing: 'border-box' }}>
                        <div className="logout-confirm-header">
                            <FaSignOutAlt className="logout-confirm-icon" />
                            <h4>Sign Out</h4>
                        </div>
                        <p className="logout-confirm-msg">Are you sure you want to sign out? Any unsaved changes will be lost.</p>
                        <div className="modal-footer" style={{ padding: '14px 0 0', margin: 0 }}>
                            <button className="btn-outline-sm" onClick={() => setShowLogoutConfirm(false)}>Cancel</button>
                            <button className="btn-logout-confirm" onClick={confirmLogout}>
                                <FaSignOutAlt /> Yes, Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="toast-container">
                {toasts.map(t => <Toast key={t.id} msg={t.msg} type={t.type} onDone={() => setToasts(p => p.filter(x => x.id !== t.id))} />)}
            </div>
        </div>
    );
};

export default HeadCaregiverDashboard;